<template>
	<div class="retirement-planner-wrap" >

    <h2>{{ _translate('retirementPlanner.title') }}</h2>

    <div class="report__client-info" v-html="showClientInfo()"></div>

    <div v-if="setStoreTables()" class="report-content">

      <div class="retirement__block">

        <div class="report__disclaimer" v-html="showDisclaimer()"></div>

        <div :class="sectionClass('outlook')">
          <h3 class="report__section-heading">
            <div class="checkbox-wrap">
              <input class="checkbox__input" type="checkbox" :checked="sectionSetChecked('outlook')" @change="sectionToggle('outlook')">
            </div>            
            <span class="report__section-label">{{ _translate('retirementPlanner.outlookTitle') }}</span>
          </h3>
          <ul v-if="this.$data.blocks.outlook" class="report__list">

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.yearsInRetirement') }}</span>
              <span class="report__value" v-html="this.getRetirementDataPoint('outlook', 
              'years')"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.investmentAssetsAtRetirement') }}</span>
              <span class="report__value" v-html="this.getRetirementDataPoint('outlook', 
              'assets')"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.targetValueOfEstate') }}</span>
              <span class="report__value" v-html="formatCurrency(this.getRetirementDataPoint('outlook', 
              'estate'))"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.shortfallExcess') }}</span>
              <span class="report__value" v-html="formatCurrency(getShortfallExcess())"></span>
            </li>

            <li v-if="getShortfallExcess() < 0" class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.requiredOneTimeInvestmentAt') }} <TextInput id="oneTimeRate" field="retirement.oneTimeRate" type="percentage"></TextInput></span>
              <span class="report__value" v-html="formatCurrency(getInvestmentRequired())"></span>
            </li>            

            <li class="report__list-item report__list-item--functional">
              <span class="report__list-label">{{ _translate('retirementPlanner.distributeWealth') }}</span>
              <span class="report__value"><button class="button" @click="minimizeExcess('income')">{{ _translate('retirementPlanner.byIncome') }}</button></span>
            </li>

          </ul>
        </div>       

        <div :class="sectionClass('income')">
          <h3 class="report__section-heading">
            <div class="checkbox-wrap">
              <input class="checkbox__input" type="checkbox" :checked="sectionSetChecked('income')" @change="sectionToggle('income')">
            </div>          
            <span class="report__section-label">{{ _translate('retirementPlanner.retirementIncomeRequired') }}</span>
          </h3>
          <ul v-if="this.$data.blocks.income" class="report__list">

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.presentAge') }}</span>
              <span class="report__value" v-html="getClientAge()"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.presentYearlyIncome') }}</span>
              <span class="report__value" v-html="formatCurrency(getBaseField('income.presentIncome'))"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.ageToRetire') }}</span>
              <span class="report__value" v-html="getRetirmentAge()"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.targetIncomeAtRetirement') }}</span>
              <span class="report__value" v-html="formatPercentage(getProjectedFirstIncomePercentage())"></span>
            </li>            

            <li class="report__list-item">
              <span class="report__list-label">{{ _prints(_translate('retirementPlanner.incomeAtRetirement'), getInitialAge()) }}</span>
              <span class="report__value" v-html="formatCurrency(getProjectedFirstIncome())"></span>
            </li>            

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.ageOfPassiveRetirement') }}</span>
              <span class="report__value" v-html="getBaseField('retirement.passiveRetirementAge')"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.incomeRequiredInPassiveRetirement') }}</span>
              <span class="report__value" v-html="formatPercentage(Number(getBaseField('retirement.passiveIncomeRequired')))"></span>
            </li>            

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.projectedRateOfInflation') }}</span>
              <span class="report__value" v-html="getInflationRate()"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.projectedRateOfReturn') }}</span>
              <span class="report__value" v-html="getRateOfReturn()"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.estimatedLifeExpectancy') }}</span>
              <span class="report__value" v-html="getBaseField('retirement.lifeExpectancy')"></span>
            </li>

          </ul>
        </div>

        <div :class="sectionClass('government')">
          <h3 class="report__section-heading">
            <div class="checkbox-wrap">
              <input class="checkbox__input" type="checkbox" :checked="sectionSetChecked('government')" @change="sectionToggle('government')">
            </div>            
            <span class="report__section-label">{{ _translate('retirementPlanner.governmentBenefits') }}</span>
          </h3>
          <ul v-if="this.$data.blocks.government" class="report__list">

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.currentMaximumCppPayout') }}</span>
              <span class="report__value" v-html="formatCurrency(getMaxCPPPayout())"></span>
            </li>  

            <li class="report__list-item">
              <span class="report__list-label">{{ _prints(_translate('retirementPlanner.projectedEligibleCppAtAgeOf'), getBaseField('retirement.cppAtAge')) }}</span>
              <span class="report__value" v-html="formatCurrency(getCppPayout(getBaseField('retirement.cppAtAge')))"></span>
            </li> 

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.indexCppAt') }}</span>
              <span class="report__value" v-html="formatPercentage(getBaseField('retirement.cppIndex'))"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.currentMaximumOasPayout') }}</span>
              <span class="report__value" v-html="formatCurrency(getMaxOASPayout())"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _prints(_translate('retirementPlanner.projectedOasAtTheAgeOf'), getBaseField('retirement.oasAtAge')) }}</span>
              <span class="report__value" v-html="formatCurrency(getOasPayout(getBaseField('retirement.oasAtAge')))"></span>
            </li>         

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.indexOasAt') }}</span>
              <span class="report__value" v-html="formatPercentage(getBaseField('retirement.oasIndex'))"></span>
            </li>           

          </ul>
        </div>

        <div :class="sectionClass('company')">
          <h3 class="report__section-heading">
            <div class="checkbox-wrap">
              <input class="checkbox__input" type="checkbox" :checked="sectionSetChecked('company')" @change="sectionToggle('company')">
            </div>            
            <span class="report__section-label">{{ _translate('retirementPlanner.pensionTitle') }}</span>
          </h3>
          <ul v-if="this.$data.blocks.company" class="report__list">

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.startReceivingPensionAtAge') }}</span>
              <span class="report__value" v-html="getBaseField('retirement.pensionAge')"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.integrateWithCpp') }}</span>
              <span class="report__value" v-html="getYesNo('retirement.cppIntegrate')"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.indexPensionAt') }}</span>
              <span class="report__value" v-html="formatPercentage(getBaseField('retirement.pensionIndex'))"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.projectedInitialBenefit') }}</span>
              <span class="report__value" v-html="formatCurrency(getPensionPayout(getBaseField('retirement.pensionAge')))"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.pensionBridgeAmount') }}</span>
              <span class="report__value" v-html="getYesNo('retirement.hasBridgeBenefit')"></span>
            </li>            

            <li v-if="getBaseField('retirement.hasBridgeBenefit') === 'true'" class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.brdigeBenefitAmount') }}</span>
              <span class="report__value" v-html="formatCurrency(getBaseField('retirement.bridgeValue') * 12)"></span>
            </li>

            <li v-if="getBaseField('retirement.hasBridgeBenefit') === 'true'" class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.bridgeBenefitStarts') }}</span>
              <span class="report__value" v-html="getBaseField('retirement.bridgeStart')"></span>
            </li>  

            <li v-if="getBaseField('retirement.hasBridgeBenefit') === 'true'" class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.bridgeBenefitEnds') }}</span>
              <span class="report__value" v-html="getBaseField('retirement.bridgeEnd')"></span>
            </li>                                     

          </ul>
        </div>

        <div :class="sectionClass('regularInvestments')">
          <h3 class="report__section-heading">
            <div class="checkbox-wrap">
              <input class="checkbox__input" type="checkbox" :checked="sectionSetChecked('regularInvestments')" @change="sectionToggle('regularInvestments')">
            </div>            
            <span class="report__section-label">{{ _translate('retirementPlanner.savingsInvestmentTitle') }}</span>
          </h3>
          <ul v-if="this.$data.blocks.regularInvestments" class="report__list">

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryCurrentValue') }}</span>
              <span class="report__value" v-html="formatCurrency(getAssetGroupingCurrentYearTotal('open'))"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryTotalValueAtRetirement') }}</span>
              <span class="report__value" v-html="formatCurrency(getAssetGroupingFinalYearTotal('open'))"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryTotalContributions') }}</span>
              <span class="report__value" v-html="formatCurrency(getAccountsContributions('open'))"></span>
            </li> 

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryRateOfReturn') }}</span>
              <span class="report__value" v-html="formatPercentage(getAccountsReturnRate('open'))"></span>
            </li>              

          </ul>
        </div>                                   

        <div :class="sectionClass('tfsaInvestments')">
          <h3 class="report__section-heading">
            <div class="checkbox-wrap">
              <input class="checkbox__input" type="checkbox" :checked="sectionSetChecked('tfsaInvestments')" @change="sectionToggle('tfsaInvestments')">
            </div>            
            <span class="report__section-label">{{ _translate('retirementPlanner.tfsaTitle') }}</span>
          </h3>
          <ul v-if="this.$data.blocks.tfsaInvestments" class="report__list">

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryCurrentValue') }}</span>
              <span class="report__value" v-html="formatCurrency(getAssetGroupingCurrentYearTotal('tfsa'))"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryTotalValueAtRetirement') }}</span>
              <span class="report__value" v-html="formatCurrency(getAssetGroupingFinalYearTotal('tfsa'))"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryTotalContributions') }}</span>
              <span class="report__value" v-html="formatCurrency(getAccountsContributions('tfsa'))"></span>
            </li> 

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryRateOfReturn') }}</span>
              <span class="report__value" v-html="formatPercentage(getAccountsReturnRate('tfsa'))"></span>
            </li>             

          </ul>
        </div> 

        <div :class="sectionClass('groupInvestments')">
          <h3 class="report__section-heading">
            <div class="checkbox-wrap">
              <input class="checkbox__input" type="checkbox" :checked="sectionSetChecked('groupInvestments')" @change="sectionToggle('groupInvestments')">
            </div>            
            <span class="report__section-label">{{ _translate('retirementPlanner.registeredInvestmentsTitle') }}</span>
          </h3>
          <ul v-if="this.$data.blocks.groupInvestments" class="report__list">

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryCurrentValue') }}</span>
              <span class="report__value" v-html="formatCurrency(getAssetGroupingCurrentYearTotal('rrsp_nodpsp'))"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryTotalValueAtRetirement') }}</span>
              <span class="report__value" v-html="formatCurrency(getAssetGroupingFinalYearTotal('rrsp_nodpsp'))"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryTotalContributions') }}</span>
              <span class="report__value" v-html="formatCurrency(getAccountsContributions('rrsp_nodpsp'))"></span>
            </li> 

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryRateOfReturn') }}</span>
              <span class="report__value" v-html="formatPercentage(getAccountsReturnRate('rrsp_nodpsp'))"></span>
            </li>                         

          </ul>
        </div> 

        <div :class="sectionClass('dpspInvestments')">
          <h3 class="report__section-heading">
            <div class="checkbox-wrap">
              <input class="checkbox__input" type="checkbox" :checked="sectionSetChecked('dpspInvestments')" @change="sectionToggle('dpspInvestments')">
            </div>            
            <span class="report__section-label">{{ _translate('retirementPlanner.dpspTitle') }}</span>
          </h3>
          <ul v-if="this.$data.blocks.dpspInvestments" class="report__list">

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryCurrentValue') }}</span>
              <span class="report__value" v-html="formatCurrency(getAssetGroupingCurrentYearTotal('dpsp'))"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryTotalValueAtRetirement') }}</span>
              <span class="report__value" v-html="formatCurrency(getAssetGroupingFinalYearTotal('dpsp'))"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryTotalContributions') }}</span>
              <span class="report__value" v-html="formatCurrency(getAccountsContributions('dpsp'))"></span>
            </li> 

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryRateOfReturn') }}</span>
              <span class="report__value" v-html="formatPercentage(getAccountsReturnRate('dpsp'))"></span>
            </li>            

          </ul>
        </div>         

        <div :class="sectionClass('lockedinInvestments')">
          <h3 class="report__section-heading">
            <div class="checkbox-wrap">
              <input class="checkbox__input" type="checkbox" :checked="sectionSetChecked('lockedinInvestments')" @change="sectionToggle('lockedinInvestments')">
            </div>            
            <span class="report__section-label">{{ _translate('retirementPlanner.lockedinInvetmentsTitle') }}</span>
          </h3>
          <ul v-if="this.$data.blocks.lockedinInvestments" class="report__list">

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryCurrentValue') }}</span>
              <span class="report__value" v-html="formatCurrency(getAssetGroupingCurrentYearTotal('lockedin'))"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryTotalValueAtRetirement') }}</span>
              <span class="report__value" v-html="formatCurrency(getAssetGroupingFinalYearTotal('lockedin'))"></span>
            </li>

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryTotalContributions') }}</span>
              <span class="report__value" v-html="formatCurrency(getAccountsContributions('lockedin'))"></span>
            </li> 

            <li class="report__list-item">
              <span class="report__list-label">{{ _translate('retirementPlanner.summaryRateOfReturn') }}</span>
              <span class="report__value" v-html="formatPercentage(getAccountsReturnRate('lockedin'))"></span>
            </li>            

          </ul>
        </div> 

      </div>

      <div v-if="getWealthAccumulationData().length > 0" class="retirement__block">      

        <div :class="sectionClass('wealthAccumulation')">
          <h3 class="report__section-heading">
            <div class="checkbox-wrap">
              <input class="checkbox__input" type="checkbox" :checked="sectionSetChecked('wealthAccumulation')" @change="sectionToggle('wealthAccumulation')">
            </div>            
            <span class="report__section-label">{{ _translate('retirementPlanner.wealthAccumulation') }}</span>
            <span v-if="this.$data.blocks.wealthAccumulation" class="section__buttons">
              <button :class="buttonClass('wealthAccumulation', 'list')" @click="toggleView('wealthAccumulation', 'list')"><Icon icon="view-list"></Icon></button>
              <button :class="buttonClass('wealthAccumulation', 'table')" @click="toggleView('wealthAccumulation', 'table')"><Icon icon="view-table"></Icon></button>
            </span>
          </h3>
          <Chart v-if="this.$data.blocks.wealthAccumulation && showWealthAccumulation()" type="bar" stacked="true" :data="chartWealthAccumulation()" id="wealthAccumulationChart"></Chart>
          <div class="report__disclaimer" v-html="showDisclaimer()"></div>          
          <div class="retirement__table-wrap">
          <table class="retirement__table list" v-if="this.$data.blocks.wealthAccumulation && getWealthAccumulationData() !== [] && this.$data.views.wealthAccumulation === 'list'">
            <tr class="retirement__table-row retirement__table-row--heading">
              <th class="retirement__table-cell retirement__table-cell--age">{{ _translate('retirementPlanner.age') }}</th>
              <th class="retirement__table-cell">{{ _translate('retirementPlanner.details') }}</th>
            </tr>
            <tr class="retirement__table-row" v-for="(item, index) in getWealthAccumulationData()"> 
              <td class="retirement__table-cell retirement__table-cell--age">
                <span v-html="getTableItem(item, 'age')"></span>
              </td>
              <td class="retirement__table-cell">              
                
                <div v-if="showColumn('wealthAccumulation', 'open')" class="retirement__table-detail">
                  <span class="retirement__table-detail-label">{{ _translate('retirementPlanner.savings') }}:</span> <span class="retirement__table-detail-value" v-html="formatCurrency(getTableItem(item, 'open'))"></span>
                </div>
                
                <div v-if="showColumn('wealthAccumulation', 'rrsp')" class="retirement__table-detail">
                  <span class="retirement__table-detail-label">{{ _translate('retirementPlanner.registered') }}:</span> <span class="retirement__table-detail-value" v-html="formatCurrency(getTableItem(item, 'rrsp'))"></span>
                </div>
                
                <div v-if="showColumn('wealthAccumulation', 'tfsa')" class="retirement__table-detail">
                  <span class="retirement__table-detail-label">{{ _translate('retirementPlanner.tfsa') }}:</span> <span class="retirement__table-detail-value" v-html="formatCurrency(getTableItem(item, 'tfsa'))"></span>
                </div>

                <div v-if="showColumn('wealthAccumulation', 'lockedin')" class="retirement__table-detail">
                  <span class="retirement__table-detail-label">{{ _translate('retirementPlanner.lockedin') }}:</span> <span class="retirement__table-detail-value" v-html="formatCurrency(getTableItem(item, 'lockedin'))"></span>
                </div>

              </td>
            </tr>
          </table>
          <table class="retirement__table tabular" v-if="this.$data.blocks.wealthAccumulation && getWealthAccumulationData() !== [] && this.$data.views.wealthAccumulation === 'table'">
            <tr class="retirement__table-row retirement__table-row--heading">
              <th class="retirement__table-cell">{{ _translate('retirementPlanner.age') }}</th>
              <th v-if="showColumn('wealthAccumulation', 'open')" class="retirement__table-cell">{{ _translate('retirementPlanner.savings') }}</th>
              <th v-if="showColumn('wealthAccumulation', 'lockedin')" class="retirement__table-cell">{{ _translate('retirementPlanner.lockedin') }}</th>
              <th v-if="showColumn('wealthAccumulation', 'rrsp')" class="retirement__table-cell">{{ _translate('retirementPlanner.registered') }}</th>
              <th v-if="showColumn('wealthAccumulation', 'tfsa')" class="retirement__table-cell">{{ _translate('retirementPlanner.tfsa') }}</th>
            </tr>
            <tr class="retirement__table-row" v-for="(item, index) in getWealthAccumulationData()"> 
              <td class="retirement__table-cell" v-html="getTableItem(item, 'age')"></td>
              <td v-if="showColumn('wealthAccumulation', 'open')" class="retirement__table-cell" v-html="formatCurrency(getTableItem(item, 'open'))"></td>
              <td v-if="showColumn('wealthAccumulation', 'lockedin')" class="retirement__table-cell" v-html="formatCurrency(getTableItem(item, 'lockedin'))"></td>
              <td v-if="showColumn('wealthAccumulation', 'rrsp')" class="retirement__table-cell" v-html="formatCurrency(getTableItem(item, 'rrsp'))"></td>
              <td v-if="showColumn('wealthAccumulation', 'tfsa')" class="retirement__table-cell" v-html="formatCurrency(getTableItem(item, 'tfsa'))"></td>              

            </tr>
          </table>
          </div>         
        </div>

      </div>

      <div class="retirement__block">      

        <div :class="sectionClass('systematicWithdrawals')">
          <h3 class="report__section-heading">
            <div class="checkbox-wrap">
              <input class="checkbox__input" type="checkbox" :checked="sectionSetChecked('systematicWithdrawals')" @change="sectionToggle('systematicWithdrawals')">
            </div>            
            <span class="report__section-label">{{ _translate('retirementPlanner.deaccumulation') }}</span>
            <span v-if="this.$data.blocks.systematicWithdrawals" class="section__buttons">
              <button :class="buttonClass('systematicWithdrawals', 'list')" @click="toggleView('systematicWithdrawals', 'list')"><Icon icon="view-list"></Icon></button>
              <button :class="buttonClass('systematicWithdrawals', 'table')" @click="toggleView('systematicWithdrawals', 'table')"><Icon icon="view-table"></Icon></button>
            </span>
          </h3>
          <Chart v-if="this.$data.blocks.systematicWithdrawals && showSystematicWithdrawals()" type="bar" stacked="true" :data="chartSystematicWithdrawals()" id="systematicWithdrawalsChart"></Chart>
          <div class="report__disclaimer" v-html="showDisclaimer()"></div>        
          <div class="retirement__table-wrap">
          <table class="retirement__table list" v-if="this.$data.blocks.systematicWithdrawals && getSystematicWidthdrawlsData() !== [] && this.$data.views.systematicWithdrawals === 'list'">
            <tr class="retirement__table-row retirement__table-row--heading">
              <th class="retirement__table-cell retirement__table-cell--age">{{ _translate('retirementPlanner.age') }}</th>
              <th class="retirement__table-cell">{{ _translate('retirementPlanner.remainingWealth') }}</th>
            </tr>
            <tr class="retirement__table-row" v-for="(item, index) in getSystematicWidthdrawlsData()"> 
              <td class="retirement__table-cell retirement__table-cell--age">
                <span v-html="getTableItem(item, 'age')"></span>
              </td>
              <td class="retirement__table-cell">              
                
                <div v-if="showColumn('systematicWithdrawals', 'savings')" class="retirement__table-detail">
                  <span class="retirement__table-detail-label">{{ _translate('retirementPlanner.savings') }}:</span> <span class="retirement__table-detail-value" v-html="formatCurrency(getTableItem(item, 'savings'))"></span>
                </div>
                
                <div v-if="showColumn('systematicWithdrawals', 'rrsp')" class="retirement__table-detail">
                  <span class="retirement__table-detail-label">{{ _translate('retirementPlanner.registered') }}:</span> <span class="retirement__table-detail-value" v-html="formatCurrency(getTableItem(item, 'rrsp'))"></span>
                </div>
                
                <div v-if="showColumn('systematicWithdrawals', 'tfsa')" class="retirement__table-detail">
                  <span class="retirement__table-detail-label">{{ _translate('retirementPlanner.tfsa') }}:</span> <span class="retirement__table-detail-value" v-html="formatCurrency(getTableItem(item, 'tfsa'))"></span>
                </div>

                <div v-if="showColumn('systematicWithdrawals', 'lockedin')" class="retirement__table-detail">
                  <span class="retirement__table-detail-label">{{ _translate('retirementPlanner.lockedin') }}:</span> <span class="retirement__table-detail-value" v-html="formatCurrency(getTableItem(item, 'lockedin'))"></span>
                </div>

              </td>
            </tr>
          </table>          
          <table class="retirement__table tabular" v-if="this.$data.blocks.systematicWithdrawals && getSystematicWidthdrawlsData() !== [] && this.$data.views.systematicWithdrawals === 'table'">
            <tr class="retirement__table-row retirement__table-row--heading">
              <th class="retirement__table-cell">{{ _translate('retirementPlanner.age') }}</th>
              <th class="retirement__table-cell">{{ _translate('retirementPlanner.year') }}</th>
              <th v-if="showColumn('systematicWithdrawals', 'savings')" class="retirement__table-cell">{{ _translate('retirementPlanner.savings') }}</th>
              <th v-if="showColumn('systematicWithdrawals', 'lockedin')" class="retirement__table-cell">{{ _translate('retirementPlanner.lockedin') }}</th>
              <th v-if="showColumn('systematicWithdrawals', 'rrsp')" class="retirement__table-cell">{{ _translate('retirementPlanner.registered') }}</th>
              <th v-if="showColumn('systematicWithdrawals', 'tfsa')" class="retirement__table-cell">{{ _translate('retirementPlanner.tfsa') }}</th>
            </tr>
            <tr class="retirement__table-row" v-for="(item, index) in getSystematicWidthdrawlsData()"> 
              <td class="retirement__table-cell" v-html="getTableItem(item, 'age')"></td>
              <td class="retirement__table-cell" v-html="getTableItem(item, 'year')"></td>
              <td v-if="showColumn('systematicWithdrawals', 'savings')" class="retirement__table-cell" v-html="formatCurrency(getTableItem(item, 'savings'))"></td>
              <td v-if="showColumn('systematicWithdrawals', 'lockedin')" class="retirement__table-cell" v-html="formatCurrency(getTableItem(item, 'lockedin'))"></td>
              <td v-if="showColumn('systematicWithdrawals', 'rrsp')" class="retirement__table-cell" v-html="formatCurrency(getTableItem(item, 'rrsp'))"></td>
              <td v-if="showColumn('systematicWithdrawals', 'tfsa')" class="retirement__table-cell" v-html="formatCurrency(getTableItem(item, 'tfsa'))"></td>              
            </tr>
          </table>
          </div>
        </div>

      </div> 

      <div class="retirement__block">

        <div :class="sectionClass('postRetirementCashFlow')">
          <h3 class="report__section-heading">
            <div class="checkbox-wrap">
              <input class="checkbox__input" type="checkbox" :checked="sectionSetChecked('postRetirementCashFlow')" @change="sectionToggle('postRetirementCashFlow')">
            </div>            
            <span class="report__section-label">{{ _translate('retirementPlanner.postRetirementCashFlows') }}</span>
            <span v-if="this.$data.blocks.postRetirementCashFlow" class="section__buttons">
              <button :class="buttonClass('postRetirementCashFlow', 'list')" @click="toggleView('postRetirementCashFlow', 'list')"><Icon icon="view-list"></Icon></button>
              <button :class="buttonClass('postRetirementCashFlow', 'table')" @click="toggleView('postRetirementCashFlow', 'table')"><Icon icon="view-table"></Icon></button>
            </span>            
          </h3>
          <Chart v-if="this.$data.blocks.postRetirementCashFlow && showPostRetirementCashFlowData()" type="bar" stacked="true" :data="chartPostRetirementCashFlowData()" id="postRetirementCashFlowChart"></Chart>
          <div class="report__disclaimer" v-html="showDisclaimer()"></div>        
          <div class="retirement__table-wrap">
          <table class="retirement__table list" v-if="this.$data.blocks.postRetirementCashFlow && getPostRetirementCashFlowData() !== [] && this.$data.views.postRetirementCashFlow === 'list'">
            <tr class="retirement__table-row retirement__table-row--heading">
              <th class="retirement__table-cell retirement__table-cell--age">Age</th>
              <th class="retirement__table-cell">Cash Flows</th>
            </tr>
            <tr class="retirement__table-row" v-for="(item, index) in getPostRetirementCashFlowData()"> 
              <td class="retirement__table-cell retirement__table-cell--age">
                <span v-html="getTableItem(item, 'age')"></span>
              </td>
              <td class="retirement__table-cell">              
                
                <div v-if="showColumn('postRetirementCashFlow', 'savings')" class="retirement__table-detail">
                  <span class="retirement__table-detail-label">{{ _translate('retirementPlanner.savings') }}:</span> <span class="retirement__table-detail-value" v-html="formatCurrency(getTableItem(item, 'savings'))"></span>
                </div>
                
                <div v-if="showColumn('postRetirementCashFlow', 'rrsp')" class="retirement__table-detail">
                  <span class="retirement__table-detail-label">{{ _translate('retirementPlanner.registered') }}:</span> <span class="retirement__table-detail-value" v-html="formatCurrency(getTableItem(item, 'rrsp'))"></span>
                </div>

                <div v-if="showColumn('postRetirementCashFlow', 'tfsa')" class="retirement__table-detail">
                  <span class="retirement__table-detail-label">{{ _translate('retirementPlanner.tfsa') }}:</span> <span class="retirement__table-detail-value" v-html="formatCurrency(getTableItem(item, 'tfsa'))"></span>
                </div>                
                
                <div v-if="showColumn('postRetirementCashFlow', 'lockedin')" class="retirement__table-detail">
                  <span class="retirement__table-detail-label">{{ _translate('retirementPlanner.lockedin') }}:</span> <span class="retirement__table-detail-value" v-html="formatCurrency(getTableItem(item, 'lockedin'))"></span>
                </div>

                <div v-if="showColumn('postRetirementCashFlow', 'cpp')" class="retirement__table-detail">
                  <span class="retirement__table-detail-label">{{ _translate('retirementPlanner.cpp') }}:</span> <span class="retirement__table-detail-value" v-html="formatCurrency(getTableItem(item, 'cpp'))"></span>
                </div>

                <div v-if="showColumn('postRetirementCashFlow', 'oas')" class="retirement__table-detail">
                  <span class="retirement__table-detail-label">{{ _translate('retirementPlanner.oas') }}:</span> <span class="retirement__table-detail-value" v-html="formatCurrency(getTableItem(item, 'oas'))"></span>
                </div>

                <div v-if="showColumn('postRetirementCashFlow', 'pension')" class="retirement__table-detail">
                  <span class="retirement__table-detail-label">{{ _translate('retirementPlanner.pension') }}:</span> <span class="retirement__table-detail-value" v-html="formatCurrency(getTableItem(item, 'pension'))"></span>
                </div>                

                <div v-if="showColumn('postRetirementCashFlow', 'other')" class="retirement__table-detail">
                  <span class="retirement__table-detail-label">{{ _translate('retirementPlanner.other') }}:</span> <span class="retirement__table-detail-value" v-html="formatCurrency(getTableItem(item, 'other'))"></span>
                </div>

              </td>
            </tr>
          </table>
          <table class="retirement__table tabular" v-if="this.$data.blocks.postRetirementCashFlow && getPostRetirementCashFlowData() !== [] && this.$data.views.postRetirementCashFlow === 'table'">
            <tr class="retirement__table-row retirement__table-row--heading">
              <th class="retirement__table-cell">{{ _translate('retirementPlanner.age') }}</th>
              <th class="retirement__table-cell">{{ _translate('retirementPlanner.year') }}</th>
              <th v-if="showColumn('postRetirementCashFlow', 'available')" class="retirement__table-cell">{{ _translate('retirementPlanner.income') }}</th>
              <th v-if="showColumn('postRetirementCashFlow', 'savings')" class="retirement__table-cell">{{ _translate('retirementPlanner.savings') }}</th>
              <th v-if="showColumn('postRetirementCashFlow', 'rrsp')" class="retirement__table-cell">{{ _translate('retirementPlanner.registered') }}</th>
              <th v-if="showColumn('postRetirementCashFlow', 'tfsa')" class="retirement__table-cell">{{ _translate('retirementPlanner.tfsa') }}</th>
              <th v-if="showColumn('postRetirementCashFlow', 'lockedin')" class="retirement__table-cell">{{ _translate('retirementPlanner.lockedin') }}</th>
              <th v-if="showColumn('postRetirementCashFlow', 'cpp')" class="retirement__table-cell">{{ _translate('retirementPlanner.cpp') }}</th>
              <th v-if="showColumn('postRetirementCashFlow', 'oas')" class="retirement__table-cell">{{ _translate('retirementPlanner.oas') }}</th>
              <th v-if="showColumn('postRetirementCashFlow', 'pension')" class="retirement__table-cell">{{ _translate('retirementPlanner.pension') }}</th>
              <th v-if="showColumn('postRetirementCashFlow', 'other')" class="retirement__table-cell">{{ _translate('retirementPlanner.other') }}</th>
            </tr>
            <tr class="retirement__table-row" v-for="(item, index) in getPostRetirementCashFlowData()"> 
              <td class="retirement__table-cell" v-html="getTableItem(item, 'age')"></td>               
              <td class="retirement__table-cell" v-html="getTableItem(item, 'year')"></td>                             
              <td v-if="showColumn('postRetirementCashFlow', 'available')" class="retirement__table-cell" v-html="formatCurrency(getTableItem(item, 'available'))"></td>
              <td v-if="showColumn('postRetirementCashFlow', 'savings')" class="retirement__table-cell" v-html="formatCurrency(getTableItem(item, 'savings'))"></td>
              <td v-if="showColumn('postRetirementCashFlow', 'rrsp')" class="retirement__table-cell" v-html="formatCurrency(getTableItem(item, 'rrsp'))"></td>
              <td v-if="showColumn('postRetirementCashFlow', 'tfsa')" class="retirement__table-cell" v-html="formatCurrency(getTableItem(item, 'tfsa'))"></td>              
              <td v-if="showColumn('postRetirementCashFlow', 'lockedin')" class="retirement__table-cell" v-html="formatCurrency(getTableItem(item, 'lockedin'))"></td>
              <td v-if="showColumn('postRetirementCashFlow', 'cpp')" class="retirement__table-cell" v-html="formatCurrency(getTableItem(item, 'cpp'))"></td>
              <td v-if="showColumn('postRetirementCashFlow', 'oas')" class="retirement__table-cell" v-html="formatCurrency(getTableItem(item, 'oas'))"></td>
              <td v-if="showColumn('postRetirementCashFlow', 'pension')" class="retirement__table-cell" v-html="formatCurrency(getTableItem(item, 'pension'))"></td>
              <td v-if="showColumn('postRetirementCashFlow', 'other')" class="retirement__table-cell" v-html="formatCurrency(getTableItem(item, 'other'))"></td>
            </tr>
          </table> 
          </div>         
        </div> 

      </div>     

    </div> <!-- END -->

  </div>
</template>

<script>

import Icon from '../Icon.vue'
import Chart from '../Chart.vue'
import TextInput from '../TextInput.vue'

export default {


  /**
   * COMPONENTS
   * -------
   * 
   * Defining an object of child components that should be made available to this report
   */
  
	components: {

    Icon,
    Chart,
    TextInput,

	}, 

  /**
   * METHODS
   * -------
   * 
   * These are the methods that are available to the component.
   */
   
  methods : {

    /**
     * METHOD: setStoreTables()
     * @return {boolean} true
     *
     * The function is intended to define tables based on the information in the store, not to set values 
     * in the store. This is a key difference, as we want the data in these tables to be a vanilla JavaScript 
     * object, not a Vue object. The reason here is to avoid unintentional reactive loops that can cause the 
     * component to continually update itself when the store is updated. 
     *
     * Always returns true;
     *
     */
    setStoreTables() {

      window.tables.wealthAccumulationData     = this.setWealthAccumulationData();
      window.tables.systematicWithdrawalsData  = this.setSystematicWidthdrawlsData();
      window.tables.postRetirementCashFlowData = this.setPostRetirementCashFlowData(); 
      window.tables.retirementSummary          = this.setRetirementSummary();

      console.log(window.tables.postRetirementCashFlowData);

      return true;

    },


    // setStoreTable(table, data) {

    //   this.$store.dispatch('saveDataTable', {
    //     table: table,
    //     data:  data,
    //   });

    // },

    
    /**
     * METHOD: placeholderFunction()
     * @return {string} the basic string placeholder
     *
     * This function returns a basic placeholder field. 
     * Use primarily in development as a placeholder for generated text in the report.
     */
    placeholderFuncion() {

      return '--';

    },




    /**
     * ---------------------------------------------------------------------------- /
     * 
     * SECTION / UI METHODS
     *
     * These methods are used to control the output of different sections within
     * the report, and assign certain dynamic classes to UI elements within the 
     * report.
     * 
     * --------------------------------------------------------------------------- */
    

    /**
     * METHOD: sectionClass()
     * @param  {String} block - the block that we're supposed to set the class for
     * @return {String} - the string of classes to be included.
     *
     * This method is used to generate the classes for the major sections in this
     * report component. It checks to see whether the section should be hidden.
     *
     * DEPRECATED: The check for whether the block should be included in the print
     * output is partially deprecated, as browser-based printing is no longer the
     * primary means of generating printed output. This condition is maintained
     * here to allow browser print fallback.
     */
    sectionClass(block) {

      var c = "report__section";

      if ( ! this.$data.blocks[block] ) {

        c += ' report__section--hidden';

      }

      else if ( this.$data.print[block]) {

        c += ' report__section--print';

      }

      return c;

    },
    

    /**
     * METHOD: sectionSetChecked()
     * @param  {String} block - the block that we're supposed to set the class for
     * @return {String/Boolean} - a value determining whether or not the checkbox
     * component should be checked.
     *
     * This method is used to determine whether or not the section visibility checkboxes 
     * are checked. The function checks the report component's data model and and updates
     * the value accordingly. 
     */
    sectionSetChecked(block) {

      var c = 'checked';

      if ( ! this.$data.blocks[block] ) {

        c = false;

      }

      return c;

    },


    /**
     * METHOD: sectionToggle()
     * @param  {String} block - the block that we're supposed to set the class for
     *
     * This function is used to change the visibility of a section within the report
     * components data model. It simply inverts the boolean value of the supplied
     * report value within the data model.
     */
    sectionToggle(block) {

      this.$data.blocks[block] = ! this.$data.blocks[block];

    },


    /**
     * METHOD: showWealthAccumulation()
     * @return Boolean - based on the method's logic
     *
     * This method is used to determined whether or not the wealth accumulation chart
     * should be rendered within the retirement planner component. It works by
     * accessing the projection data to determine of it has any values. If it does
     * not, the method determines that that chart should not be rendered.
     */
    showWealthAccumulation() {

      var rawData = this.getWealthAccumulationData(),
          total = 0;

      for ( var x = 0; x < rawData.length; x++ ) {

        total += rawData[x].open;
        total += rawData[x].lockedin;
        total += rawData[x].rrsp;
        total += rawData[x].realestate;
        total += rawData[x].other;

      }

      if ( total <= 0 ) {

        return false;

      }

      return true;

    }, 


    /**
     * METHOD: showSystematicWithdrawals()
     * @return Boolean - based on the method's logic
     *
     * This method is used to determined whether or not the wealth de-accumulation chart
     * should be rendered within the retirement planner component. It works by
     * accessing the projection data to determine of it has any values. If it does
     * not, the method determines that that chart should not be rendered.
     */
    showSystematicWithdrawals() {

      var rawData = this.getSystematicWidthdrawlsData(),
          total = 0;

      for ( var x = 0; x < rawData.length; x++ ) {

        total += rawData[x].savings;
        total += rawData[x].lockedin;
        total += rawData[x].tfsa;
        total += rawData[x].rrsp;

      }

      if ( total <= 0 ) {

        return false;

      }

      return true;

    }, 


    /**
     * METHOD: showPostRetirementCashFlowData()
     * @return Boolean - based on the method's logic
     *
     * This method is used to determined whether or not the cashflow chart
     * should be rendered within the retirement planner component. It works by
     * accessing the projection data to determine of it has any values. If it does
     * not, the method determines that that chart should not be rendered.
     */
    showPostRetirementCashFlowData() {

      var rawData = this.getPostRetirementCashFlowData(),
          total = 0;

      for ( var x = 0; x < rawData.length; x++ ) {

        total += rawData[x].savings;
        total += rawData[x].lockedin;
        total += rawData[x].rrsp;
        total += rawData[x].tfsa;
        total += rawData[x].cpp;
        total += rawData[x].other;

      }

      if ( total <= 0 ) {

        return false;

      }

      return true;

    },


    /**
     * METHOD: showColumn()
     * @param  String source [description]
     * @param  String column [description]
     * @return Boolean - whether or not the column should be rendered.
     *
     * This method checks to determine if there is any value within a 
     * "column" in the data table. If the column is empty, it tells the
     * application not to render the column in the data table.
     * 
     */
    showColumn(source = 'wealthAccumulation', column) {

      var data = {},
          i = 0,
          val = '';

      if ( source === 'wealthAccumulation' ) {

        data = this.getWealthAccumulationData();
        i = data.length - 1;
        var val = data[i][column];

      }

      else if ( source === 'systematicWithdrawals' ) {

        data = this.getSystematicWidthdrawlsData();
        var val = data[i][column];

      }

      else if ( source === 'postRetirementCashFlow' ) {

        data = this.getPostRetirementCashFlowData();
        val = 0;

        for ( var x = 0; x < data.length; x++ ) {

          if ( data[x][column] !== undefined ) {

            val += Number(data[x][column]);

          }

        }

      }

      if ( val !== undefined && val > 0 ) {

        return true;

      }

      return false;

    },


    /**
     * METHOD: showDisclaimer()
     * @return String - the disclaimer for inclusion at the top of the report.
     *
     * Returns the disclaimer to be included in the report.
     * Not visually rendered in the application.
     * 
     * PARTIAL DEPRECATION: This method was included in the application when the reports
     * were printed via the browser's built-in print functionality. Since the PDF
     * generation, that functionality is seldom used, but the functionality remains
     * for legacy support.
     */
    showDisclaimer() {

      return this.$store.getters.getDisclaimer();

    },


    /**
     * METHOD: showClientInfo()
     * @return String - the client info text
     *
     * Returns the client info block for inclusion at the top of the report.
     * Not visually rendered in the application.
     * 
     * PARTIAL DEPRECATION: This method was included in the application when the reports
     * were printed via the browser's built-in print functionality. Since the PDF
     * generation, that functionality is seldom used, but the functionality remains
     * for legacy support.
     */
    showClientInfo() {

      var name = this.$store.getters.getCurrentClientName();
          name.trim();

      var html = '<div class="report__client-name">' + name + '</div>';

      html += this.$store.getters.getAddress({field: 'details.address'});

      return html;

    }, 

    
    /**
     * METHOD: toggleView()
     * @param  {String} view - the view section that the method should set
     * @param  {String} value - the value that should be applied to the view section
     *
     * This method is used to change the view data element for different sections
     * in the component's data model.
     */
    toggleView(view, value) {

      this.$data.views[view] = value;

    },


    /**
     * METHOD: buttonClass()
     * @param  {String} view - the view section that the method should set
     * @param  {String} value - the value that should be applied to the view section
     * @return {String} - returns the class for the button, as determined by the view
     * and value parameters
     */
    buttonClass(view, value) {

      var c = 'section__button';

      if ( this.$data.views[view] === value ) {

        c += ' js-active';

      }

      return c;

    },


    /**
     * ---------------------------------------------------------------------------- /
     * 
     * FORMATTING METHODS
     *
     * These methods are used to format the output of certain types of values
     * within the report.
     * 
     * --------------------------------------------------------------------------- */
    

    /**
     * METHOD: formatCurrency()
     * @param  {number} val - the numeric value that should be converted
     * @return {string} the value, formatted as a currency string
     *
     * This function is designed to connect into the global formatCurrency()
     * method defined at the window level.
     */
    formatCurrency(val) {

      return window.formatCurrency(val);

    },


    /**
     * METHOD: formatPercentage()
     * @param  {number} number - the numeric value that should be converted
     * @param  {boolean} approximate - define whether approximate output should be used
     * @return {string} the value, formatted as a percentage
     *
     * This function is designed to convert a numeric value into a percentage string.
     * If the approximate parameter is set to true, this will append a tilde to the 
     * beginning of the string to imply that the percentage is only approximate. 
     */
    formatPercentage(number, approximate = false) {

      number = Number(number);
      number *= 100;

      // check for approximate use
      if ( approximate ) {

        if ( number.toFixed(2) > 0 ) {

          return '~' + number.toFixed(2) + "%";

        }

      }

      return number.toFixed(2) + "%";

    },



    /**
     * ---------------------------------------------------------------------------- /
     * 
     * SUMMARY METHODS
     *
     * These methods are used to format the output of certain types of values
     * within the report.
     * 
     * --------------------------------------------------------------------------- */

    /**
     * METHOD: setRetirementSummary()
     * @return {object} the full object of generated values
     *
     * This method gathers a number of key data points, as required by the component and
     * stores them to a singular object for easy access.
     */
    setRetirementSummary() {

      return window.rawObject({

          // Define data points in the Outlook section
          outlook: {

            years: this.getClientRetirementToDeath(),
            assets: window.formatCurrency(this.getRetirementCapitalAvailable(true), false),
            estate: this.getBaseField('retirement.estateValue'),
            shortfall: window.formatCurrency(this.getShortfallExcess(), false),
            investment: (this.getShortfallExcess() < 0) ? window.formatCurrency(this.getInvestmentRequired(), false) : false,
            investmentRate: (this.getShortfallExcess() < 0) ? window.formatPercentage(this.getBaseField('retirement.oneTimeRate')) : false,

          },

          // Define data points in the Retirement section
          retirement: {

            age: this.getClientAge(),
            presentIncome: window.formatCurrency(this.getBaseField('income.presentIncome'), false),
            ageRetire: this.getRetirmentAge(),
            targetIncomePercent: window.formatPercentage(this.getProjectedFirstIncomePercentage()),
            targetIncome: window.formatCurrency(this.getProjectedFirstIncome(), false),
            agePassive: this.getBaseField('retirement.passiveRetirementAge'),
            targetPassivePercent: window.formatPercentage(this.getBaseField('retirement.passiveIncomeRequired')),
            lifeExpectancy: this.getBaseField('retirement.lifeExpectancy'),
            rateInflation: this.getInflationRate(),
            rateReturn: this.getRateOfReturn(),

          },

          // Define data points in the Government section
          government: {

            currentMaxCPP: window.formatCurrency(this.getMaxCPPPayout()),
            ageCPP: this.getBaseField('retirement.cppAtAge'),
            projectCPP: window.formatCurrency(this.getCppPayout(this.getBaseField('retirement.cppAtAge'))),
            indexCPP: window.formatPercentage(this.getBaseField('retirement.cppIndex')),
            currentMaxOAS: window.formatCurrency(this.getMaxOASPayout()),
            ageOAS: this.getBaseField('retirement.oasAtAge'),
            projectOAS: window.formatCurrency(this.getOasPayout(this.getBaseField('retirement.oasAtAge'))),
            indexOAS: window.formatPercentage(this.getBaseField('retirement.oasIndex')),

          },

          // Define data points in the Pension section
          pension: {

            pensionAge: this.getBaseField('retirement.pensionAge'),
            integrateCPP: this.getBaseField('retirement.cppIntegrate'),
            pensionIndex: window.formatPercentage(this.getBaseField('retirement.pensionIndex')),
            projectedBenefit: window.formatCurrency(this.getPensionPayout(this.getBaseField('retirement.pensionAge'))),
            hasBridge: this.getBaseField('retirement.hasBridgeBenefit'),
            bridgeAmount: window.formatCurrency(this.getBaseField('retirement.bridgeValue') * 12),
            bridgeStart: (this.getBaseField('retirement.hasBridgeBenefit') === 'true') ? this.getBaseField('retirement.bridgeStart') : false,
            bridgeEnd: (this.getBaseField('retirement.hasBridgeBenefit') === 'true') ? this.getBaseField('retirement.bridgeEnd') : false,


          },

          // Define data points in the Investments sections
          investments: {

            regularValue: window.formatCurrency(this.getAssetGroupingCurrentYearTotal('open')),
            regularProjected: window.formatCurrency(this.getAssetGroupingFinalYearTotal('open')),
            regularContribute: window.formatCurrency(this.getAccountsContributions('open')),
            regularReturn: window.formatPercentage(this.getAccountsReturnRate('open')),
            regularPrint: (this.getAccountsReturnRate('open') === 0) ? false : true,

            tfsaValue: window.formatCurrency(this.getAssetGroupingCurrentYearTotal('tfsa')),
            tfsaProjected: window.formatCurrency(this.getAssetGroupingFinalYearTotal('tfsa')),
            tfsaContribute: window.formatCurrency(this.getAccountsContributions('tfsa')),
            tfsaReturn: window.formatPercentage(this.getAccountsReturnRate('tfsa')),
            tfsaPrint: (this.getAccountsReturnRate('tfsa') === 0) ? false : true,

            registeredValue: window.formatCurrency(this.getAssetGroupingCurrentYearTotal('rrsp_nodpsp')),
            registeredProjected: window.formatCurrency(this.getAssetGroupingFinalYearTotal('rrsp_nodpsp')),
            registeredContribute: window.formatCurrency(this.getAccountsContributions('rrsp_nodpsp')),
            registeredReturn: window.formatPercentage(this.getAccountsReturnRate('rrsp_nodpsp')),
            registeredPrint: (this.getAccountsReturnRate('rrsp_nodpsp') === 0) ? false : true,

            dpspValue: window.formatCurrency(this.getAssetGroupingCurrentYearTotal('dpsp')),
            dpspProjected: window.formatCurrency(this.getAssetGroupingFinalYearTotal('dpsp')),
            dpspContribute: window.formatCurrency(this.getAccountsContributions('dpsp')),
            dpspReturn: window.formatPercentage(this.getAccountsReturnRate('dpsp')),
            dpspPrint: (this.getAccountsReturnRate('dpsp') === 0) ? false : true,

            lockedinValue: window.formatCurrency(this.getAssetGroupingCurrentYearTotal('lockedin')),
            lockedinProjected: window.formatCurrency(this.getAssetGroupingFinalYearTotal('lockedin')),
            lockedinContribute: window.formatCurrency(this.getAccountsContributions('lockedin')),
            lockedinReturn: window.formatPercentage(this.getAccountsReturnRate('lockedin')),
            lockedinPrint: (this.getAccountsReturnRate('lockedin') === 0) ? false : true,

          },

      });

    },


    /**
     * METHOD: getRetirementDataPoint()
     * @param {string} k1 - the key of the data group that we want to return
     * @param {string} k2 - the key of the data point that we want to return within the k1 group
     * @return {various} the value of the data point in the retirementSummary object
     *
     * This method attempts to get and return a specific value from the retirementSummary
     * table. Generally used for displaying these values within the component report.
     */
    getRetirementDataPoint(k1, k2) {

      if ( k1 === undefined || k2 === undefined ) {

        return '';

      }

      var point = window.tables.retirementSummary[k1][k2];

      if ( point === undefined ) {

        return '';

      }

      return point;

    },


    /**
     * ---------------------------------------------------------------------------- /
     * 
     * Basic Getters / Calculators
     *
     * These are simple methods that are used to fetch data from Vuex getters
     * (which may themselves be more complex methods), OR, which execute simple
     * calculations based on store data.
     *
     * --------------------------------------------------------------------------- */


    /**
     * METHOD: getBaseField()
     * @param {string} field - the name of the field to be returned, usually using dot (.) notation
     * @return {various} the value of the defined field
     *
     * This method calls the getFieldData() getter method, which itself attempts to get a
     * basic data point from the store. Field names generally use the basic dot (.) notation
     * pattern that is used throughout the application.
     * 
     * Example: income.presentIncome
     */    
    getBaseField(field) {

      return this.$store.getters.getFieldData({field: field});

    },


    /**
     * METHOD: getConstant()
     * @param {string} constant - the name of the field to be returned, usually using dot (.) notation
     * @return {various} the value of the defined field
     *
     * This method calls the getConstants() getter method, which returns an object containing all of
     * the data constants within the application. It then returns the data of the specified data point
     * using the key supplied via the constant parameter.
     */ 
    getConstant(constant) {

      var c = this.$store.getters.getConstants();

      return c[constant];

    },


    /**
     * METHOD: getYesNo()
     * @param  {String} field - the name of the field to be returned, usually using dot (.) notation
     * @return {String} - return the (translated) string for Yes or No
     *
     * Takes the value of a boolean field and translates it as "Yes" if the value is true and
     * "No" if the value is anything other than true.
     */
    getYesNo(field) {

      var val = this.$store.getters.getFieldData({field: field});

      if ( val === 'true' ) {

        return this._translate('ui.yes');

      }

      return this._translate('ui.no');

    },


    /**
     * METHOD: getClientDOB()
     * @return {Object} - the DOB object
     *
     * This method calls the getClientDOB() getter function to retrieve the client's date of birth.
     */
    getClientDOB() {

      return this.$store.getters.getClientDOB();

    },
    

    /** 
     * METHOD: getClientAge()
     * @return {Number} - the client age
     *
     * This method calls the getClientAge() getter function to retrieve the client's current age.
     */
    getClientAge() {

      return this.$store.getters.getClientAge();

    },



    /**
     * METHOD: getClientStartAge()
     * @return {Number} - the client age at the beginning of the plan
     *
     * This method calls the getClientStartAge() getter function to retrieve the client's age 
     * at the beginning of the retirement plan. This can change, based on snapshot data.
     */
    getClientStartAge() {

      return this.$store.getters.getClientStartAge(false);

    },


    /**
     * METHOD: getClientCurrentToDeath()
     * @return {Number} - the number of years remaining until assumed death.
     *
     * This method calls the getClientCurrentToDeath() getter function to retrieve the 
     * number of years until assumed death. It uses the getClientStartAge() to determine
     * this value, meaning that it is also subject change, based on snapshot data. 
     */
    getClientCurrentToDeath() {

      return this.$store.getters.getClientCurrentToDeath(false);

    },


    /**
     * METHOD: getClientWithdrawalAge()
     * @return {Number} - the age at which the client will begin withdrawing funds from their accounts
     *
     * This method calls the getClientWithdrawalAge() getter function to retrieve the
     * age at which the client will being withdrawing funds.
     */
    getClientWithdrawalAge() {

      return this.$store.getters.getClientWithdrawalAge(false);

    },


    /**
     * METHOD: getRetirmentAge()
     * @return {Number} - the age at which the client will retire
     *
     * This method calls the getRetirmentAge() getter function to retrieve the
     * age at which the client will retire. This value is determined either by the
     * explicit age value or by the retirment date field.
     */
    getRetirmentAge() {

      return this.$store.getters.getRetirmentAge();

    },    


    /**
     * METHOD: getClientWidrawaltoDeath()
     * @return {Number} - the number of years between retirement (withdrawal age) and death.
     *
     * This method calls the getClientWidrawaltoDeath() getter function to retrieve the
     * number of years between the beginning of withdrawals and the projected age of death.
     */
    getClientWidrawaltoDeath() {

      return this.$store.getters.getClientWidrawaltoDeath(false);

    },


    /**
     * METHOD: getClientYearsToRetire()
     * @return {Number} - the number of years until the client retires.
     *
     * This method calls the getClientYearsToRetire() getter function to retrieve the
     * number of years until the client retires.
     */
    getClientYearsToRetire() {

      return this.$store.getters.getClientYearsToRetire(false);

    },


    /**
     * METHOD: getClientWidrawaltoDeath()
     * @return {Number} - the number of years between retirement (withdrawal age) and death.
     *
     * This method calls the getClientWidrawaltoDeath() getter function to retrieve the
     * number of years between retirement and the projected age of death.
     */
    getClientRetirementToDeath() {

      return this.$store.getters.getClientRetirementToDeath();

    },


    /**
     * METHOD: getRetirmentCapitalRequired()
     * @return {Number} - The total value of capital required for retirement.
     *
     * This method calls the getRetirmentCapitalRequired() getter function to retrieve
     * the total capital required in retirement.
     */
    getRetirmentCapitalRequired() {

      return this.$store.getters.getRetirmentCapitalRequired();

    },


    /**
     * METHOD: getInflationRate()
     * @return {Number} - returns the inflation index percentage.
     *
     * This method calls the getInflationRate() getter method to retrieve the 
     */
    getInflationRate() {

      return this.$store.getters.getIndexPercentage();

    },    


    getInitialAge() {

      var val = 0,
          rawData = window.tables.postRetirementCashFlowData;

      if ( rawData.length > 0 ) {    
        val = rawData[0].age;
      }

      return val;      

    },

    getProjectedFirstIncome() {

      var val = 0,
          rawData = window.tables.postRetirementCashFlowData;

      if ( rawData.length > 0 ) {    
        val = rawData[0].available;
      }

      return val;

    },

    getProjectedFirstIncomePercentage() {

      var income = this.getProjectedFirstIncome(),
          inflated = this.getInflatedIncome();

      return income / inflated;

    },

    getBlendedRate(sections) {

      var rate = 0,
          total = 0,
          breakdown = [];

      // get total values
      for ( var x = 0; x < sections.length; x++ ) {

        total += sections[x].val;

      }

      // set percentages of values
      for ( var x = 0; x < sections.length; x++ ) {

        breakdown[x] = sections[x].val / total;

      }

      // set percentages of interest rates
      for ( var x = 0; x < sections.length; x++ ) {

        rate += (sections[x].ror * breakdown[x]);

      }

      return rate;

    },

    getRateOfReturn() {

      var capital    = this.$store.getters.getInitialCapital(),
          goal       = this.$store.getters.getRetirementCapitalAvailable(),
          periods    = this.$store.getters.getClientYearsToRetire(false),
          contribute = this.$store.getters.getTotalContributions(),

          // Get the number of years to retire
          toRetire = this.$store.getters.getClientYearsToRetire(false),

          // Set the base rate
          rate = 0;

      // If the total years to retire is greater than 0, the client is NOT in retirement,
      // so we can calculate based on growth projections
      if (toRetire > 0) {

        // rate = this.$store.getters.getInterestRateRequired(
        //   periods + 1,
        //   contribute,
        //   capital,
        //   goal,
        //   0
        // );

        // Get the raw table data
        var rawData = window.tables.wealthAccumulationData;

        // Loop
        var y = 0;
        for ( var x = 0; x < rawData.length; x++ ) {

            if ( rawData[x].ror > 0 ) {

              rate += rawData[x].ror;
              y += 1;

            } 

        }

        // Return the average
        rate /= (y === 0) ? 1 : y;

      }

      // Otherwise, the client IS in retirement, so we have to calculate this value
      // based on the running total that we calculated during post-retirement cashflow
      // calculations
      else {

        // Get the raw table data
        var rawData = window.tables.postRetirementCashFlowData;

        // Loop
        var y = 1;
        for ( var x = 1; x < rawData.length; x++ ) {

          if ( rawData[x].ror.total > 0 ) {

            rate += rawData[x].ror.total;
            y += 1;

          } 

        }

        // Return the average
        rate /= (y-1 === 0) ? 1 : (y-1);

      }

      return this.formatPercentage(rate);

    },


    /**
     * METHOD: getRetirementIncomeRequiredByYear()
     * @return [array] Returns an array of all the income values, year by year.
     *
     * Gets the total value of income, required year by year throughout retirement.
     */
    getRetirementIncomeRequiredByYear() {

      return this.$store.getters.getRetirementIncomeRequiredByYear();

    },


    /**
     * METHOD: getIncomeAtRetirement()
     * @return [number] Returns to total income required for the first
     * year of retirement
     * 
     * This method determines the required income at retirement, by
     * taking the current income, indexing it across the years between
     * the current date and the retirement date (as determined by the
     * retirement age), then multiplied by the percentage required at
     * retirement value 
     */
    getIncomeAtRetirement() {

      var value = this.getInflatedIncome(),
          percentage = this.getBaseField('retirement.requiredAtRetirement');

      return value * percentage;      


    },


    /**
     * METHOD: getInflatedIncome()
     * @return {Number} - returns the inflated income amount.
     *
     * This method takes the present client income and applies the inflation rate to the value
     * to determine the projected relative value at retirement.
     *
     * NOTE: This is not necessarily the income required at retirement, just the inflated value
     * of the current income. Income requirements can be defined as a percentage of this value. 
     */
    getInflatedIncome() {

      var today = new Date(),
          dob   = this.getClientDOB(),

          retirement_age = parseInt(this.getRetirmentAge()),
          retirement     = new Date(parseInt(dob.year) + retirement_age, dob.month-1, dob.day),

          income = this.getBaseField('income.presentIncome'),
          i      = Number(this.getBaseField('retirement.inflationRate')),

          value = income;

      for ( var x = today.getFullYear(); x < retirement.getFullYear(); x++ ) {

        value *= (1 + i);

      }

      return value;

    },


    /**
     * METHOD: getWealthAtReirementData()
     * @return {Object} - object containing wealth data for the final year of growth.
     *
     * This method takes the data object generated by the getAssetsGrowth() method and carves out
     * the values of the FINAL year, into its own object, which can be used to process the
     * wealth at retirement.
     */
    getWealthAtReirementData() {

      var raw_data = this.getAssetsGrowth(),
          end_data = {};

          for ( var i in raw_data ) {

            end_data[i] = [];
            for ( var x = 0; x < raw_data[i].length; x++ ) {

              end_data[i][x] = raw_data[i][x][raw_data[i][x].length - 1];

            }

          }

      return end_data;

    },


    /**
     * METHOD: getRetirementCapitalAvailable() 
     * @param  {Boolean} exclude - whether or not to exclude strictly capital (non-account) assets
     * @return {Number} - the total amount of capital available at retirement.
     *
     * This method calls the getRetirementCapitalAvailable() getter function to get the
     * total amount of capital available at retirement. The exclude parameter is simply passed
     * into the getter method, which contains the logic to change behavior based on this 
     * boolean value. 
     */
    getRetirementCapitalAvailable(exclude = false) {

      return this.$store.getters.getRetirementCapitalAvailable(exclude);

    },


    /**
     * METHOD: getMaxOASPayout()
     * @param  {Boolean} projected - determines whether or not the value should be projected
     * @return {Number} - returns the value of the max OAS payout.
     *
     * This method calls the getMaxOASPayout() getter function to get the maximum OAS
     * payout, based on the constant values. The optional projection value controls whether or
     * not the function projects the value based on the inflation rate. 
     */
    getMaxOASPayout(projected = false) {

      return this.$store.getters.getMaxOASPayout(projected);

    },


    /**
     * METHOD: getOasPayout()
     * @param  {Number} age - the age for which we should determine the OAS payout
     * @return {Number} - returns the amount of OAS that should be paid out at a given age.
     *
     * This method calls the getOasPayout() getter function to get the exact value that
     * is assumed to be available at a given age.
     */
    getOasPayout(age = false) {

      return this.$store.getters.getOasPayout(age);

    },

    
    /**
     * METHOD: getMaxCPPPayout()
     * @return {Number} - returns the value of the max CPP payout.
     *
     * This method calls the getMaxCPPPayout() getter function to get the maximum CPP
     * payout, based on the constant values. 
     */
    getMaxCPPPayout() {

      return this.$store.getters.getMaxCPPPayout();

    },

    
    /**
     * METHOD: getCppPayout()
     * @param  {Number} age - the age for which we should determine the CPP payout
     * @param  {Boolean} reduct - [DEPRECATED] 
     * @return {Number} - returns the amount of CPP that should be paid out at a given age.
     *
     * This method calls the getCppPayout() getter function to get the exact value that
     * is assumed to be available at a given age.
     */
    getCppPayout(age = false, reduct = false) {

      return this.$store.getters.getCppPayout(age, reduct);

    },

    
    /**
     * METHOD: getPensionPayout()
     * @param  {Number} age - the age for which we should determine the pension payout
     * @return {Number} - returns the amount of pension that should be paid out at a given age.
     *
     * This method calls the getPensionPayout() getter function to get the exact value that
     * is assumed to be available at a given age.
     */
    getPensionPayout(age = false) {

      return this.$store.getters.getPensionPayout(age);

    },


    /**
     * METHOD: getOtherIncome()
     * @return {Number} - The total amount of "other" income.
     *
     * This method simply returns the value of total income from other sources, as 
     * set in the client profile.
     */
    getOtherIncome() {

      return Number(this.getBaseField('income.otherSources'));

    },


    /**
     * METHOD: getDecumulationsOrder()
     * @return {Object} - an object containing the account identifiers
     * 
     * This method calls the getDecumulationsOrder() getter function, arranged in
     * the order of withdrawal, as determined by the client profile.
     */
    getDecumulationsOrder() {

      return this.$store.getters.getDecumulationsOrder();

    },



    /**
     * METHOD: getTableItem()
     * @param  Array  item - the item that we should return
     * @param  String property - the key for the property that we should return
     * @return the specific property within the supplied item.
     *
     * This method is specifically intended to be able to return the value of an array within
     * the v-html style property in Vue's templates. 
     */
    getTableItem(item, property) {

      return item[property];

    },



    /**
     * ---------------------------------------------------------------------------- /
     * 
     * Tabular Data Functions
     *
     * These functions are intended to generate and retrieve tabular data that is used
     * to generate the report's projections.
     *
     * --------------------------------------------------------------------------- */    


    /**
     * METHOD: getWealthAccumulationData()
     * @return {Object} data object
     *
     * Simple access method that returns the wealthAccumulationData table from the
     * global window.tables object.
     */
    getWealthAccumulationData() {

      return window.tables.wealthAccumulationData;

    },


    /**
     * METHOD: setWealthAccumulationData()
     * @return {Object} - returns an object containing the wealth accumulation
     * projection data.
     *
     * This method initiates logical loop, which iterates over the years leading
     * up to retirement and applies wealth projection data for each valid year.
     */
    setWealthAccumulationData() {

      var data = [],
          age = this.getClientStartAge(false),
          years = this.getClientYearsToRetire(false),

          _age        = 0,
          _savings    = 0,
          _lockedin   = 0,
          _tfsa       = 0,
          _rrsp       = 0,
          _ror        = 0;

      var raw_data = this.getAssetsGrowth();

      if ( years > 0 ) {

        for (var x = 0; x < years; x++) {

          _age        = x + age;
          _savings    = this.getAssetGroupingTotal(raw_data, x, 'open');
          _lockedin   = this.getAssetGroupingTotal(raw_data, x, 'lockedin');
          _rrsp       = this.getAssetGroupingTotal(raw_data, x, 'rrsp');
          _tfsa       = this.getAssetGroupingTotal(raw_data, x, 'tfsa');
          _ror        = this.getBlendedRate([_savings, _lockedin, _rrsp, _tfsa]);

          data[x] = {
            age:        _age + 1,
            open:       _savings.val,
            lockedin:   _lockedin.val,
            rrsp:       _rrsp.val,
            tfsa:       _tfsa.val,
            ror:        _ror,
          };

        }

      }

      return data;

    },


    /**
     * METHOD: getSystematicWidthdrawlsData()
     * @return {Object} data object
     *
     * Simple access method that returns the systematicWithdrawalsData table from the
     * global window.tables object.
     */
    getSystematicWidthdrawlsData() {

      return window.tables.systematicWithdrawalsData;

    },


    /**
     * METHOD: setSystematicWidthdrawlsData()
     * @return {Object} - returns an object containing the wealth de-accumulation
     * projection data.
     *
     * This method initiates logical loop, which iterates over the years following
     * retirement and applies wealth change projection data for each valid year.
     * 
     * NOTE: While the method is intended to chart de-accumulation, if there is
     * sufficient wealth and a high enough interest, this method can actually return
     * an object that projects continued growth.
     */
    setSystematicWidthdrawlsData() {

      var data           = [],
          age            = this.getClientWithdrawalAge(), //parseInt(this.getBaseField('retirement.retirementAge')),
          years          = this.getClientWidrawaltoDeath(),
          wealth         = this.getWealthAtReirementData(),
          incomeRequired = this.getRetirementIncomeRequiredByYear(),
          dob            = this.getBaseField('details.dateOfBirth'),

          _age       = 0,
          _savings   = 0,
          _lockedin  = 0,
          _rrsp      = 0,
          _tfsa      = 0;

      // Loop over the years of retirement

      for (var x = 0; x < years; x++) {

        _age = x + age;

        // Decumulate the wealth
        var i = incomeRequired[_age];
        var w = window.rawObject(wealth);

        // Accumulate wealth for the year
        if ( x > 0 ) {
          wealth = window.rawObject(this.getPostRetirementWealthAccumulation(window.rawObject(w)));
        }

        wealth = window.rawObject(this.getPostRetirementWealthDecumulation(window.rawObject(wealth), i, _age, false));

        // Accumulate the wealth with any yearly growth

        // Total the individual blocks
        _savings   = this.getAssetGroupingTotal(wealth, false, 'open');
        _lockedin  = this.getAssetGroupingTotal(wealth, false, 'lockedin');
        _rrsp  = this.getAssetGroupingTotal(wealth, false, 'rrsp');
        _tfsa  = this.getAssetGroupingTotal(wealth, false, 'tfsa');

        data[x] = {
          age:      _age + 1,
          year:     Number(dob['year']) + _age,
          savings:  _savings.val,
          lockedin: _lockedin.val,
          rrsp:     _rrsp.val,
          tfsa:     _tfsa.val,
        };

      }      

      return data;

    }, 


    /**
     * METHOD: getPostRetirementCashFlowData()
     * @return {Object} data object
     *
     * Simple access method that returns the postRetirementCashFlowData table from the
     * global window.tables object.
     */
    getPostRetirementCashFlowData() {

      return window.rawObject(window.tables.postRetirementCashFlowData);

    },


    /**
     * METHOD: setPostRetirementCashFlowData()
     * @return {Object} - returns an object containing the information about income sources.
     *
     * This method initiates logical loop, which iterates over the years following
     * retirement and determines the income sources for each year.
     * 
     * NOTE: This method is slightly more complex than the setSystematicWidthdrawlsData()
     * method, it ALSO makes use of the core getPostRetirementWealthDecumulation(), which
     * is the core logical component, which drives a significant portion of the report
     * component's internal logic.
     */
    setPostRetirementCashFlowData() {

      var data           = [],
          age            = this.getRetirmentAge(),
          years          = this.getClientWidrawaltoDeath(),
          wealth         = this.getWealthAtReirementData(),
          incomeRequired = this.getRetirementIncomeRequiredByYear(),
          dob            = this.getBaseField('details.dateOfBirth'),

          _income     = 0,
          _required   = 0,
          _available  = 0,
          _wealth     = 0,

          _age        = 0,
          _savings    = 0,
          _lockedin   = 0,
          _rrsp       = 0,
          _tfsa       = 0,
          _cpp        = 0,
          _oas        = 0,
          _pension    = 0,
          _other      = 0,
          _shortfall  = 0,
          _ror        = 0,
          _remainder  = 0,
          _totalsf    = 0;

      // Loop over the years of retirement

      for (var x = 0; x < years; x++) {

        _age = x + age;

        // Decumulate the wealth
        var i = incomeRequired[_age];
        var w = window.rawObject(wealth);

        // Accumulate wealth for the year
        if ( x > 0 ) {
          wealth = window.rawObject(this.getPostRetirementWealthAccumulation(window.rawObject(w)));
        }

        _ror = this.getRateofReturn(w, wealth, _age);

        wealth = this.getPostRetirementWealthDecumulation(wealth, i, _age, true);

        var sources = wealth.sources;
        wealth  = wealth.wealth;

        // Accumulate the wealth with any yearly growth

        // Total the individual blocks
        _savings    = sources.savings,
        _lockedin   = sources.lockedin;
        _rrsp       = sources.rrsp;
        _tfsa       = sources.tfsa;
        _cpp        = sources.cpp;
        _oas        = sources.oas;
        _pension    = sources.pension;
        _other      = sources.other;
        _shortfall  = (x > 0) ? data[x - 1].shortfall + sources.shortfall : sources.shortfall;
        _remainder  = sources.remainder;

        _income     = sources.income;
        _required   = sources.required;
        _available  = sources.available;
        _wealth     = sources.wealth;

        _totalsf += sources.shortfall;

        console.log(_age);

        data[x] = {
          age: _age,
          year: Number(dob['year']) + _age,
          savings:    _savings,
          lockedin:   _lockedin,
          rrsp:       _rrsp,
          tfsa:       _tfsa,
          cpp:        _cpp,
          oas:        _oas,
          pension:    _pension,          
          other:      _other,
          shortfall:  _shortfall,
          ror:        _ror,
          remainder:  _remainder,

          income:     _income,
          required:   _required,
          available:  _available,
          wealth:     _wealth,

          totalsf:    _totalsf,   

        };

      }

      return data;

    },


    /**
     * METHOD: getPostRetirementWealthDecumulation()
     * @param  {Object}  wealth - the current wealth object
     * @param  {[type]}  i - the income required for the present year
     * @param  {Number}  age - determines the client's age
     * @param  {Boolean} use_sources - determines whether or not to use the sources attribution
     * @return {Object} - the object with the updated wealth values; optionally also contains source attribution
     *
     * !IMPORTANT! This method forms one of the CORE PIECES of the component report's internal logic.
     * 
     * The method is responsible for determining the values that must be withdrawn from the various accounts
     * in any given year during retirement, as such, it drives both the de-accumulation table and the
     * post-retirement cashflows reports.
     *
     * The method is also responsible for:
     *   - accounting for CPP, OAS and Pension amounts (which effect withdrawals)
     *   - balancing the withdrawal of income from different accounts
     *   - accounting for min/max withdrawals on relevant accounts
     *   - optionally back-filling income in instances where accounts are depleted
     *
     * If the use_sources parameter is set to true, the method will return an object with child objects 
     * that include:
     *   - the wealth object (as otherwise returned on its own if use_sources is false)
     *   - the sources object, which contains the income derived from different sources. This data is
     *     used to generate the post-retirement cash flow data.
     */
    getPostRetirementWealthDecumulation(wealth, i, age, use_sources = false) {

      // Define various variables and objects that will be used throughout the method's logic.
      var cpp         = Number(this.$store.getters.getCppPayout(age)),
          oas         = Number(this.$store.getters.getOasPayout(age)),
          pension     = Number(this.$store.getters.getPensionPayout(age)),
          otherIncome = Number(this.$store.getters.getOtherIncome()),
          segment     = 0,
          remainder   = i - (oas + cpp + pension) - otherIncome,
          shortfall   = 0,

          lifMinMax   = this.getLIFMinMax(age),
          use_max     = false,

          sources   = {
            income:     i,
            required:   (remainder > 0) ? remainder : 0,
            available:  cpp + oas + pension + otherIncome,
            wealth:     0,
            savings:    0,
            rrsp:       0,
            tfsa:       0,
            lockedin:   0,
            shortfall:  0,
            remainder:  0,
            cpp:        cpp,
            oas:        oas,
            pension:    pension,
            other:      otherIncome,
          },

          order       = this.getBaseField('retirement.withdrawalOrder'),
          current_segment = 'rrsp',

          withdrawable = this.getWithdrawableAccounts(age),

          autofill = (this.getBaseField('retirement.autofillShortfall') === 'true' ) ? true : false;

      // convert the order variable into an array that we can actually use;
      order = order.split('_');

      // define the initial segment from the first item in our array;
      var o = order[0],
          order_weight = {};

      // iterate over the order array and set the order weights
      for ( var x = 0; x < order.length; x++ ) {

        switch ( order[x] ) {

          case 'l':
            order_weight['lockedin'] = x + 1;
            break;

          case 's':
            order_weight['savings'] = x + 1;
            break;

          case 't':
            order_weight['tfsa'] = x + 1;
            break;

          case 'r':
            order_weight['rrsp'] = x + 1;
            break;

        }

      }     

      // Set the initial current segment
      switch ( o ) {

        case 'l':
          current_segment = 'lockedin';
          break;

        case 's':
          current_segment = 'savings';
          break;

        case 't':
          current_segment = 'tfsa';
          break;

        case 'r':
          current_segment = 'rrsp';
          break;

      }

      // Set the minimum and maximum values for LIF-based accounts.  
      var lifAccountNames = this.getLIFAccounts(),
          lifAccounts     = {},
          decumulations   = this.getDecumulationsOrder(),
          min_totals      = 0,
          minimums        = {
            lockedin: 0,
            savings:  0,
            tfsa:     0,
            rrsp:     0,
          },
          maximums  = {
            lockedin: 0,
          },          
          segments = {
            lockedin: 1000,
            savings:  1000,
            tfsa:     1000,
            rrsp:     1000,
          },
          fields = {
            lockedin: 'retirement.fromLockedin',
            savings:  'retirement.fromSavings',
            tfsa:     'retirement.fromTfsa',
            rrsp:     'difference',
          }

      for ( var x = 0; x < lifAccountNames.length; x++ ) {

        var k = lifAccountNames[x];

        if ( wealth[k] !== undefined ) {

          lifAccounts[k] = [];

          for ( y = 0; y < wealth[k].length; y++ ) {

            var w = wealth[k][y].val;

            lifAccounts[k][y] = {
              min:    w * lifMinMax.min,
              max:    w * lifMinMax.max,
              range: (w * lifMinMax.max) - (w * lifMinMax.min),
              taken:  0,
              closed: false, 
            };

            min_totals += lifAccounts[k][y].min;
            minimums[decumulations[k]] += lifAccounts[k][y].min;

            if (k.substr(0,8) === 'lockedin') {
              maximums[decumulations[k]] += lifAccounts[k][y].max;
            }

          }

        }

      }

      // Define the initial segments list
      var minimums_array = [],
          segments_array = [];

      for ( var k in minimums ) {

        minimums_array.push({
          key: k,
          value: minimums[k],
        });

      }

      // Sort the segments by total value
      minimums_array.sort(function(a, b){

        var _a = Number(a['value']) - Number(order_weight[a['key']]),
            _b = Number(b['value']) - Number(order_weight[b['key']]);

        return _b - _a;

      });

      // Loop through the values and define whether or not the minimum is required
      for ( var x = 0; x < minimums_array.length; x++ ) {

        var k = minimums_array[x].key,
            base_segment = 
            (fields[k] === 'difference')

          ? remainder * (1 - this.getBaseField('retirement.fromLockedin') - this.getBaseField('retirement.fromSavings') - this.getBaseField('retirement.fromTfsa'))

          : remainder * Number(this.getBaseField(fields[k])),

        min = minimums[k];
        max = (maximums[k]) ? maximums[k] : null;

        segments[k] = {
          base: base_segment,
          min:  min,
          max:  max,
        }  

      }

      // Attempt to balance the required values, based on minimums and the withdrawal order
      if ( remainder < 0 ) { remainder = 0; }  
      segments = this.balanceDeacumulationSegments(segments, remainder);

      // Withdraw the minimums
      for ( var k in lifAccounts ) {

        var key = decumulations[k],
            target = segments[decumulations[k]];

        for ( var y = 0; y < lifAccounts[k].length; y++ ) {

          var difference = wealth[k][y].value - lifAccounts[k][y].min;
              segment = lifAccounts[k][y].min;

          if ( use_sources ) {

            sources.wealth += segment;

          }

          wealth[k][y].val -= segment;
          segments[key] -= segment;

          lifAccounts[k][y].taken = segment;

          if ( lifAccounts[k][y].taken > target) {

            lifAccounts[k][y].closed = true;

          }

          if ( use_sources ) {

            sources[key] += segment;
            // sources.remainder += wealth[k][y];            

          }

        }

      }    

      // Loop over everything until we reach a total value of 0;
      // We want to run this loop so that we ensure that we are pulling
      // from everything as required;
      var b = 0;

      while ( segments['lockedin'] +
              segments['savings'] + 
              segments['tfsa'] +
              segments['rrsp'] > 0 &&
              b < 4 ) {

        // For each item in the data decumulations list
        for ( var k in decumulations ) {

          // Check the "category" of the asset type if it's different
          // from the previous loop...
          if ( current_segment !== decumulations[k] ) {

            // If it is, then we need to switch over to the next category
                        
            // Add the remainder of the previous segment to the new segment
            if ( autofill ) {
              segments[decumulations[k]] += segments[current_segment];
            }

            // Zero out the current segment
            segments[current_segment] = 0;

            // Set the current segment to the new segment
            current_segment = decumulations[k];

          }
    
          // Loop through the accounts within a segment
          for ( var y = 0; y < wealth[k].length; y++ ) {

            if ( ! withdrawable[k][y] ) { continue; }

            var is_open = true;

            if ( lifAccounts[k] !== undefined ) {

              if ( lifAccounts[k][y] !== undefined ) {

                is_open = ! lifAccounts[k][y].closed;

              }

            }

            if ( is_open ) {

              if ( use_sources ) {

                sources.wealth += wealth[k][y].val;

              }

              if ( wealth[k][y].val > 0  && segments[current_segment] > 0) {

                segment = segments[current_segment];

                use_max = false;

                // If this segment is from a LIF-type account, we need to make
                // sure that it falls within the range.
                if ( this.isLIFAccount(k) ) {

                  var max = lifAccounts[k][y].max - lifAccounts[k][y].taken,
                      min = lifAccounts[k][y].min;
                 
                  if ( segment > max && k.substr(0,8) === 'lockedin') {

                    segment = max;
                    lifAccounts[k][y].taken += max;
                    lifAccounts[k][y].closed = true;

                    use_max = true;

                  }

                }                      

                // If there's more in the account than required by the
                // remainder, subtract that amount
                if ( wealth[k][y].val > segment ) {                        

                  wealth[k][y].val -= segment;

                  segments[current_segment] = segments[current_segment] - segment;

                  // segments[current_segment] = (! use_max) ? segments[current_segment] - segment : 0;

                }

                // Otherwise, we need to zero out this account, and 
                // then account for the remaining required income in
                // by resetting the remainder.
                else {               

                  segment = segments[current_segment];

                  segments[current_segment] -= wealth[k][y].val;

                  wealth[k][y].val = 0;

                  if ( segment > segments[current_segment] ) {
                    segment -= segments[current_segment];
                  }
                  else {
                    segment = segments[current_segment];
                  }

                }             

                // Here we basically add the segment value to the sources data
                // object. Simple if / else if logic with multiple optional 
                // conditions.
                if ( use_sources ) {

                  if ( k === 'open_chequing' ||
                       k === 'open_savings' ||
                       k === 'open_stocks' ||
                       k === 'open_mutualFunds' ||
                       k === 'open_other' ) {

                    sources.savings += segment;

                  }

                  else if ( k === 'open_tfsa' ) {

                    sources.tfsa += segment;

                  }

                  else if ( k === 'registered_mutualFunds' ||
                       k === 'registered_rrif' ||
                       k === 'registered_dpspEsopGrsp' ||                     
                       k === 'registered_other' ) {

                    sources.rrsp += segment;

                  }

                  else if ( k === 'lockedin_lockedRrsp' ||
                       k === 'lockedin_employerPension' ) {

                    sources.lockedin += segment;

                  }

                }

              }

            } 

          }

        }

        if ( autofill ) {

          b += 1;

        }

        else {

          b += 3;

        }

      }

      sources.available += sources.lockedin + 
                           sources.savings + 
                           sources.rrsp +
                           sources.tfsa;
      
      // Iterate over the remaining segments object and add
      // all of the values into the shortfall variable.
      for ( var k in segments ) {

        shortfall += segments[k];
      }

      // If shortfall is greater than 0, this means that we were not
      // able to pull enough money from the required amounts.
      if ( shortfall > 0 ) {

        sources.shortfall += shortfall;

      } 

      if ( use_sources ) {

        for ( var k in wealth ) {

          for ( var y = 0; y < wealth[k].length; y++ ) {

            sources.remainder += wealth[k][y].val;

          }

        }

        return {

          wealth: wealth,
          sources: sources,

        }

      }

      return wealth;

    },    



    /**
     * ---------------------------------------------------------------------------- /
     * 
     * Assets Methods
     *
     * These methods are used to check, group, control and assist in the calculation
     * of growth within various assets.
     *
     * --------------------------------------------------------------------------- */ 


    /**
     * METHOD getAssetsGrowth()
     * @return Object - returns the full object of all the asset growth.
     *
     * This method is designed to generate an object with the full set of
     * asset growth data, by running the getAccoutGrowth() method (which is
     * itself a call to a method of the store) to calculate the projected
     * growth on each asset type group.
     */
    getAssetsGrowth() {

      return {

        open_chequing:            this.getAccountGrowth('savings.open.chequing'),
        open_savings:             this.getAccountGrowth('savings.open.savings'),
        open_stocks:              this.getAccountGrowth('savings.open.stocks'),
        open_mutualFunds:         this.getAccountGrowth('savings.open.mutualFunds'),
        open_other:               this.getAccountGrowth('savings.open.other'),

        open_tfsa:                this.getAccountGrowth('savings.open.tfsa'),

        registered_mutualFunds:   this.getAccountGrowth('savings.registered.mutualFunds'),
        registered_rrif:          this.getAccountGrowth('savings.registered.rrif'),
        registered_other:         this.getAccountGrowth('savings.registered.other'),
        registered_dpspEsopGrsp:  this.getAccountGrowth('savings.registered.dpspEsopGrsp'),        

        lockedin_lockedRrsp:      this.getAccountGrowth('savings.lockedin.lockedRrsp'),
        lockedin_employerPension: this.getAccountGrowth('savings.lockedin.employerPension'),

      };

    },


    /**
     * getAssetNodes()
     * @param  String type - define the type of assets to get
     * @return Array - returns the array with the requested asset nodes
     *
     * This function is used to get a listing of accounts withing a specific type
     * of account. 
     *
     * TO DO: This function would probably be written more efficiently as a
     * switch block rather than one long if/else statement.
     */
    getAssetNodes(type) {

      var nodes = [];

      if ( type === 'open' ) {

        nodes = [
          'open_chequing',
          'open_savings',
          'open_stocks',
          'open_mutualFunds',
          'open_other',
        ];

      }

      else if ( type === 'tfsa' ) {

        nodes = [
          'open_tfsa',
        ]

      }

      else if ( type === 'rrsp') {

        nodes = [
          'registered_mutualFunds',
          'registered_rrif',
          'registered_other',
          'registered_dpspEsopGrsp'          
        ];

      }

      else if ( type === 'rrsp_nodpsp') {

        nodes = [
          'registered_mutualFunds',
          'registered_rrif',
          'registered_other',
        ];

      }           

      else if ( type === 'dpsp' ) {

        nodes = [
          'registered_dpspEsopGrsp'
        ]

      }

      else if ( type === 'lockedin' ) {

        nodes = [
          'lockedin_lockedRrsp',
          'lockedin_employerPension',
        ];

      }

      else if ( type === 'realestate' ) {

        nodes = [
          'realestate_home',
          'realestate_other',
        ];

      }

      else if ( type === 'other' ) {

        nodes = [
          'other_businessInterests',
          'other_other',
        ]

      }

      return nodes;

    },


    /**
     * METHOD: getAssetGroupingTotal()
     * @param  {Object} data - the wealth data that we want to accumulate
     * @param  {String} index - the index within the wealth data to accumulate
     * @param  {String} type - the type of asset grouping
     * @return {Object} - object containing the new asset total + the rate of return
     *
     * This method is used to calculate the total project growth of a grouping of
     * accounts. It returns the accumulated value AND the projected rate of return. 
     */
    getAssetGroupingTotal(data, index, type) {

      var total = 0,
          rate = 0,
          a = 0,
          nodes = this.getAssetNodes(type);

      for ( var n = 0; n < nodes.length; n++ ) {

        var r = 0;
        for ( var x = 0; x < data[nodes[n]].length; x++ ) {

          if (index !== false) { 

            r += Number(data[nodes[n]][x][index].ror)
            total += Number(data[nodes[n]][x][index].val);

          }

          else {

            total += Number(data[nodes[n]][x].val);

          }

        }

        if ( x > 0 ) {

          rate += r/x;

        }

        if ( r > 0 ) {

          a += 1;

        }

      }

      if ( a > 0 ) {

        rate = rate/a;

      }

      return {
        val: total,
        ror: rate,
      };

    },


    /**
     * METHOD: getAssetGroupingYearTotal()
     * @param  {Number} year - the year, represented in as a number.
     * @param  {String} type - the account type
     * @return {Object} - return the object grouping total object.
     *
     * This method gets the total asset value for a specific year.
     */
    getAssetGroupingYearTotal(year, type) {

      if ( type === undefined ) {

        return 0;

      }

      var raw_data = this.getAssetsGrowth();

      return this.getAssetGroupingTotal(raw_data, year, type);

    },


    /**
     * METHOD: getAssetGroupingCurrentYearTotal()
     * @param  {String} - the account type
     * @return {Object} - return the object grouping total object.
     *
     * Gets the asset grouping for the current (first) year.
     */
    getAssetGroupingCurrentYearTotal(type) {

      var total = this.getAssetGroupingYearTotal(0, type);

      if ( isNaN(total.val) ) {

        total = this.getAssetGroupingCurrentYearTotal(type);

      }

      return total.val;

    },


    /**
     * METHOD: getAssetGroupingFinalYearTotal()
     * @param  {String} - the account type
     * @return {Object} - return the object grouping total object.
     *
     * Gets the asset grouping for the last year.
     */
    getAssetGroupingFinalYearTotal(type) {

      var years = this.getClientYearsToRetire(false),
          total = 0;

      if ( years < 0 ) {

        years = 0;

      }

      total = this.getAssetGroupingYearTotal(years, type);

      return total.val;

    },


    /**
     * METHOD: getAssetGroupingROI()
     * @param  {String} - the account type
     * @return {Number} - the calculated ROI for an asset grouping.
     *
     * DEPRACATED 
     * This function was written to calculate ROI for asset groupings, 
     * but was deprecated when the ROI logic was expanded for more
     * accurate calculation and representation.
     */
    getAssetGroupingROI(type) {

      var start = this.getAssetGroupingYearTotal(0, type),
          end   = this.getAssetGroupingFinalYearTotal(type),
          years = this.getClientYearsToRetire(false);

      if ( start === 0 || years === 0 ) {

        return 0;
        
      }

      return ( ( end / start ) - 1 ) / years;
          
    },


    /**
     * METHOD: getAccountGrowth()
     * @param  {String} field - the name of the field in dot (.) notation
     * @param  {String} v - a representation of the value to return from the repeater
     * @return {Array} - returns the array that is created by the getAccountGrowth getter
     *
     * This method calls the getAccountGrowth() getter function from the store.
     * The array contains the account growth projections year over year.
     */
    getAccountGrowth(field, v = 'balance') {

      return this.$store.getters.getAccountGrowth(field, v);

    },


    /**
     * METHOD: getAccountProperties()
     * @param  {Object} account - the account details object, which contains all the client information on the account, as
     *                  added to the repeater field in the client object
     * @param  {String} v - a representation of the property value to return from the repeater
     * @return {String} - returns the value from the getter function, which should always be a string
     */
    getAccountProperties(account, v) {

      return this.$store.getters.getAccountProperties(account, v);

    },    


    /**
     * METHOD: getRateofReturn()
     * @param  {Object} w      [description]
     * @param  {Object} wealth [description]
     * @param  {Number} age    [description]
     * @return - returns the rate of return
     *
     * This method takes the two sets of data supplied through the w
     * and wealth variables and compares their values in order to calculate
     * an associated rate of return. 
     */
    getRateofReturn(w, wealth, age) {

      var _w      = {total: 0},
          _wealth = {total: 0},
          _ror    = {total: 0},
          difference = 0;

      for ( var k in w ) {

        if ( ! _w[k] ) {
          _w[k] = 0;
        }

        for ( var x = 0; x < w[k].length; x++ ) {

          _w[k]       += Number(w[k][x].val);
          _w['total'] += Number(w[k][x].val);
 
        }

      }

      for ( var k in wealth ) {

        if ( ! _wealth[k] ) {
          _wealth[k] = 0;
        }

        for ( var x = 0; x < wealth[k].length; x++ ) {

          _wealth[k]       += Number(wealth[k][x].val);
          _wealth['total'] += Number(wealth[k][x].val);

        }
        
      }

      for ( var k in _w ) {

        if ( _wealth[k] ) {
 
          difference = _wealth[k] - _w[k];
          _ror[k] = difference / _w[k];         

        }

      }

      difference = _wealth.total - _w.total;

      _ror.total = (_w.total > 0) ? difference / _w.total : 0;

      return _ror;

    },


    /**
     * METHOD balanceDeacumulationSegments()
     * @param  {[type]} segments_array [description]
     * @param  {[type]} remainder      [description]
     * @return {[type]}                [description]
     *
     * This method attempts to take the segment balance (based on client
     * retirement details) and re-balance the required amounts, based on the
     * min/max values applied to certain account types. This balanced
     * breakdown can then be used to determine the required values from each
     * of the account types. 
     */
    balanceDeacumulationSegments(segments_array, remainder) {

      var segments = {},
          balanced = false,
          count = 0;

      // Count number of elements
      for ( var key in segments_array ) { if(segments_array.hasOwnProperty(key)) { ++count; }}

      var i = count,
          float = 0;

      while ( ! balanced && i >= 0 ) {

        var loop_balance = 0,

            // x is an iterator value, which we use to determine if we are in the
            // midst of the first loop
            x = 0, 

            // f is a value that we use to control the floating values
            f = 0;
        
        // Loop over every segment in the segments array    
        for ( var k in segments_array ) {

          // First Loop, where we set the values
          if (segments[k] === undefined) {

            // If there is more in the minimum than in the base amount, then we have to
            // do some magic, setting the value to the minimum and then setting a float
            // value so we can de-accumulate additional accounts
            if ( segments_array[k].min > segments_array[k].base ) {

              segments[k]   = segments_array[k].min;
              loop_balance += segments_array[k].min;

              f = segments_array[k].min - segments_array[k].base;
              float += f;

            }

            // If we have a define maximum value (ie not null), AND this maximum value 
            // is less than the base amount, we need to re-balance again
            else if ( segments_array[k].max !== null && segments_array[k].max < segments_array[k].base ) {

              segments[k] = segments_array[k].max;

              f = segments_array[k].max - segments_array[k].base;
              float += f;

            }

            // Otherwise, we can just use what's there
            else {

              segments[k]   = segments_array[k].base;
              loop_balance += segments_array[k].base;             

            }

          }

          if ( x === i  && float > 0 && segments_array[k].min < segments_array[k].base) {

            segments[k]  -= float;
            loop_balance -= float;

            if ( segments[k] < 0 ) {

              float = segments[k] * -1;
              segments[k] = 0;

            }
            else {

              float = 0;

            }

          }

          else if ( x === i && float < 0 && segments_array[k].max < segments_array[k].base ) {

            segments[k]  -= float;
            loop_balance -= float;

            if ( segments[k] < 0 ) {

              float = segments[k];
              segments[k] = 0;

            }

            else {

              float = 0;

            }

          }

          x += 1;

        }

        if ( float === 0 ) {

          balanced = true;

        }

        i -= 1;

      }

      return JSON.parse(JSON.stringify(segments));

    },


    /**
     * METHOD: getPostRetirementWealthAccumulation()
     * @param  {Object} wealth - the current wealth object
     * @return {Object} - the de-accumulated wealth object
     *
     * This method accepts a wealth object and then processes accumulation that occurs
     * in post-retirement. It then returns the updated wealth object.
     */
    getPostRetirementWealthAccumulation(wealth) {

      for ( var k in this.$data.decumulations ) {

        for ( var y = 0; y < wealth[k].length; y++ ) {

          if (wealth[k][y]) {

            var f          = 'savings.' + k.replace('_', '.'),
                accounts   = this.$store.getters.getRepeaterData({field: f}),
                vars       = this.getAccountProperties(accounts[y], 'balance');

            vars.c = 0;
            wealth[k][y] = this.$store.getters.getAccountGrowthLine(wealth[k][y].val, vars);

          }

        }        

      }

      return wealth;

    },


    /**
     * METHOD: getAccountsContributions()
     * @param  {[type]}  type - the type of account
     * @param  {Boolean} life - whether or not to calculate the lifetime contributions
     * @return {Number} - returns the total value that is contributed
     *
     * This method works to fetch the dollar amount that is contributed to a general
     * type of account. It is built to optionally calculate the lifetime contributions
     * or just a single year of contribution.
     *
     * The application assumes that contributions will be happening on a consistent
     * basis in pre-retirement. 
     */
    getAccountsContributions(type, life = true) {

      var total = 0,
          nodes = this.getAssetNodes(type),
          years = this.getClientYearsToRetire(false),
          per_year = 0,
          contributions = this.$store.getters.getAssetsContributions();

      for ( var x = 0; x < nodes.length; x++ ) {

        for ( var y = 0; y < contributions[nodes[x]].length; y++ ) {

          per_year += Number(contributions[nodes[x]][y]);

        }

      }

      if ( life ) {

        for ( var x = 0; x < years; x++ ) {

          total += per_year;

        }

      }

      else {

        total = per_year;

      }

      return total;

    },


    /**
     * METHOD: getAccountsReturnRate()
     * @param  {String} type - the type of account
     * @return {Number} - the rate of return calculated by the method
     *
     * This method is used to determine the projected rate of return. It uses two different
     * forms of logic to do this, based on whether the client is currently in retirement.
     */
    getAccountsReturnRate(type) {

      var periods    = this.getClientYearsToRetire(false),
          starting   = this.getAssetGroupingCurrentYearTotal(type),
          final      = this.getAssetGroupingFinalYearTotal(type),
          contribute = this.getAccountsContributions(type, false),
          gain       = final - starting - contribute,

          // Get the number of years to retire
          toRetire = this.$store.getters.getClientYearsToRetire(false);         

      // If the client has not yet reached the age of retirement, we can use the getter function
      // getInterestRateRequired, which accepts some static values and runs a simple calculation
      // based on those numbers.
      if (toRetire > 0) {

        var rate = this.$store.getters.getInterestRateRequired (
          periods,
          contribute,
          starting,
          final,
          0
        );

      }

      // Otherwise, the client IS in retirement, so we have to calculate this value
      // based on the running total that we calculated during post-retirement cashflow
      // calculations
      else {

        // Get the raw table data
        var rawData = window.tables.postRetirementCashFlowData,
            nodes   = this.getAssetNodes(type),
            points  = {},
            periods = {},
            rate = 0;

        // Loop over the years.
        for ( var x = 1; x < rawData.length; x++ ) {

          for ( var k = 0; k < nodes.length; k++ ) {

            if ( rawData[x].ror[nodes[k]] ) {

              if ( ! points[nodes[k]] ) {

                points[nodes[k]] = 0;

              }             

              if ( ! periods[nodes[k]] ) {

                periods[nodes[k]] = 0;

              }  

              if ( rawData[x].ror[nodes[k]] > 0 ) {

                points[nodes[k]] += rawData[x].ror[nodes[k]];
                periods[nodes[k]] += 1;

              }

            }

          }

        }

        var y = 0;
        for ( k in points ) {
          rate += points[k]/periods[k];
          y += 1;
        }

        if ( rate > 0 && x > 1) { 
          // Return the average
          rate /= y;
        }

      }      

      return rate;

    },


    /** 
     * METHOD: getWithdrawableAccounts()
     * @param  {Number} age - the age to check against
     * @return {Object} - multi-dimensional object that indicates boolean values about whether
     * or not withdrawals should occur. 
     *
     * This method is used to generate an object that helps define whether accounts should be
     * drawn from at a particular age. It basically calls the getWithdrawableAccount() method
     * for each account type.
     */
    getWithdrawableAccounts(age) {

      var activeAccounts = {

        open_chequing:            this.getWithdrawableAccount(age, 'savings.open.chequing'),
        open_savings:             this.getWithdrawableAccount(age, 'savings.open.savings'),
        open_stocks:              this.getWithdrawableAccount(age, 'savings.open.stocks'),
        open_mutualFunds:         this.getWithdrawableAccount(age, 'savings.open.mutualFunds'),
        open_other:               this.getWithdrawableAccount(age, 'savings.open.other'),

        open_tfsa:                this.getWithdrawableAccount(age, 'savings.open.tfsa'),

        registered_mutualFunds:   this.getWithdrawableAccount(age, 'savings.registered.mutualFunds'),
        registered_rrif:          this.getWithdrawableAccount(age, 'savings.registered.rrif'),
        registered_other:         this.getWithdrawableAccount(age, 'savings.registered.other'),
        registered_dpspEsopGrsp:  this.getWithdrawableAccount(age, 'savings.registered.dpspEsopGrsp'),        

        lockedin_lockedRrsp:      this.getWithdrawableAccount(age, 'savings.lockedin.lockedRrsp'),
        lockedin_employerPension: this.getWithdrawableAccount(age, 'savings.lockedin.employerPension'),

      };

      return activeAccounts;

    },


    /**
     * METHOD: getWithdrawableAccount()
     * @param  {Number} age - the age to check against
     * @param  {String} f - the field to check against
     * @return {Array} - an array with boolean values indicating whether withdrawals should be taken.
     *
     * This method is used to generate an array of boolean values, which is mapped to the individual
     * accounts within the repeater structure.
     */
    getWithdrawableAccount(age, f) {

      var accounts = [];
      age = Number(age);

      var repeaterData = this.$store.getters.getRepeaterData({field: f});

      for ( var x = 0; x < repeaterData.length; x++ ) {
        var properties = this.getAccountProperties(repeaterData[x], 'startingAge'),
            startAge = properties['a'],
            activate = false;

        if ( properties['a'] ) {
          if (age >= startAge) {
            activate = true;
          }
        } else {
          activate = true;
        }

        accounts[x] = activate

      }

      return accounts;

    },


    /**
     * ---------------------------------------------------------------------------- /
     * 
     * Min/Max
     *
     * These methods are used to control the minimum and maximum withdrawals 
     * associated with the different accounts. 
     *
     * --------------------------------------------------------------------------- */     


    /**
     * METHOD: getLIFAccounts()
     * @return {Object} returns an array of account type identifiers
     *
     * This method is used to determine the list of accounts that should actively
     * be applying the min/max amounts to withdrawals. The logic checks to determine
     * whether the checkbox for each relevant account type is checked, thereby
     * providing the user with even more
     */
    getLIFAccounts() {

      var array = [];

      if ( this.getBaseField('savings.minmax_lockedin_lockedRrsp') ) { array.push('lockedin_lockedRrsp'); }
      if ( this.getBaseField('savings.minmax_lockedin_pension') ) { array.push('lockedin_employerPension'); }

      return array;  

    },


    /**
     * METHOD: isLIFAccount()
     * @param  {String}  account - the account identifier
     * @return {Boolean} - returns a boolean value indicating whether the account type should use min/max
     *
     * This method basically checks to determine whether or not an account is in the list of
     * accounts that should use the min/max logic.
     */
    isLIFAccount(account) {

      var lifs = this.getLIFAccounts();

      for ( var x = 0; x < lifs.length; x++ ) {

        if ( account === lifs[x] ) {

          return true;

        }

      }

      return false;

    },


    /**
     * METHOD: getLIFMinMax()
     * @param  {Number} age - the age for which we want to determine the min/max values
     * @return {Object} - an object including the min and max constant values for a given age
     *
     * This method is used to extract the minimum and maximum percentages for withdrawals
     * from relevant accounts. It adds the minimum and maximum values to an object, which
     * is returned for use by the rest of the component.
     */
    getLIFMinMax(age = null) {

      var min_max = {
        age: age,
        min: 0,
        max: 0,
      }

      if ( age !== null ) {

        var lifSchedule = this.getConstant('lifSchedule');
            lifSchedule = lifSchedule.rows;

        for ( var x = 0; x < lifSchedule.length; x++ ) {

          var row = {},
              k   = '';

          for ( var y = 0; y < lifSchedule[x].length; y++ ) {

            k      = lifSchedule[x][y].field;
            row[k] = lifSchedule[x][y].value

          }

          if ( Number(row.age) === Number(age) ) {

            min_max = row;
            x = lifSchedule.length;

          }

        }

      }

      return min_max;

    },    


    /**
     * ---------------------------------------------------------------------------- /
     * 
     * Shortfall Methods
     *
     * These methods are used to control the "Distribute Wealth" functionality. 
     *
     * --------------------------------------------------------------------------- */ 


    /**
     * METHOD: getShortfallExcess()
     * @return {Number} - the shortfall or excess value
     *
     * This method retrieves the final wealth value at the end of the client's life.
     * The function makes use of the Post-Retirement cash flow data, which tracks 
     * shortfalls, allowing this method to report on any total shortfall.
     */
    getShortfallExcess() {

      var data = this.getPostRetirementCashFlowData();
      return (data[data.length - 1].shortfall > 0) ? (data[data.length - 1].shortfall * -1) : data[data.length - 1].remainder;

    },


    /**
     * METHOD: getInvestmentRequired()
     * @return {Number} - the total investment required
     *
     * This method attempts to calculate a HIGHLY generalized one-time investment, as required
     * to counter-balance a projected shortfall.
     *
     * TO DO: This logic could almost certainly be improved, by hooking into some of the other
     * functions, though likely at the cost of a performance hit.
     */
    getInvestmentRequired() {

      var shortfall = Math.abs(Number(this.getShortfallExcess())),
          rate      = Number(this.getBaseField('retirement.oneTimeRate')),
          years     = Number(this.getClientYearsToRetire(false)),
          total     = shortfall;

      for ( var x = 0; x < years; x++ ) {

        total /= 1 + rate;

      }

      return total;

    },


    /**
     * METHOD: minimizeExcess()
     * TYPE: Trigger
     * @param  {string} action :: the type of action to take; only one parameter is accepted
     * @return {null} 
     * 
     * This function is intended to minimize the overall excess calculation
     * This is the basic trigger function, which then relies on 
     * minimizeExcessByIncome() to do the actual calculation and to establish
     * the recursive loop
     */
    minimizeExcess(action) {

      if ( action === 'income' ) {

        var c = 'min',
            v = 1;

        this.$store.dispatch('saveField', {
          field: 'retirement.requiredAtRetirement',
          value: v,
        });
        
        this.setStoreTables();
        var self = this;

        this.$data.calc = setTimeout(function(){

          var endpoint = Number(self.getShortfallExcess());
          self.minimizeExcessByIncome(endpoint);

        }, .5);

      }

    },


    /**
     * METHOD: minimizeExcessByIncome()
     * TYPE: Calculator
     * @param  {string} action :: the type of action to take; only one parameter is accepted
     * @return {null} 
     * 
     * This function is used to reduce the income at retirement by increments until
     * it reaches the point where there is no income left after death. The purpose
     * of the function is to maximize income available in retirement, based on the
     * current projected asset growth and other available income sources.
     *
     * This is an EXTREMELY process intensive function.
     */
    minimizeExcessByIncome(start) {

      var lastyear = window.rawObject(window.tables.postRetirementCashFlowData[window.tables.postRetirementCashFlowData.length - 1]),
          endpoint = Number(this.getShortfallExcess()),
          totalsf = lastyear.totalsf,
          percent  = Number(this.getBaseField('retirement.requiredAtRetirement')),
          percentPost = Number(this.getBaseField('retirement.passiveIncomeRequired')),
          i = Number(this.getBaseField('retirement.inflationRate')),

          threshold = Number(this.getBaseField('retirement.estateValue')),

          target = 'retirement.requiredAtRetirement',

          currentTarget = this.getRetirementCapitalAvailable(true),         

          ratio = .0001,

          diff = Math.abs(currentTarget) / Math.abs(endpoint - threshold);         

      // We don't want to have a value of less than 0 percent here.
      if ( percent < 0 ) {

        return false;

      }  

      // If our endpoint is less than 0, we're done with the calculation.
      if ( endpoint >= threshold && totalsf <= 0 ) {

        this.setStoreTables();
        return true;

      }

      // Define the target ratio reduction.
      // The idea with this logic is to determine the ratio by which to
      // reduce the overall income required at retirement in such a way
      // as to apply broad-stroke reductions, and then gradually fine-tune
      // the reduction as we zero in on the target number.
      if ( diff < 1 ) {

        ratio = .1;

      }

      else if ( diff < 25 ) {

        ratio = .01;

      }

      else if ( diff < 30 ) {

        ratio = .005;

      }

      else if ( diff < 50 ) {

        ratio = .001;

      }


      // If we're using a value that's higher than the 1.5% inflation
      // rate, we're going to reduce the increments even further, as 
      // a higher inflation rate tends to skew this process 
      if ( i > .015 ) { 
        ratio /= (i*100);
      }

      // Reduce the income required at retirement by the calculated
      // ratio.
      this.$store.dispatch('saveField', {
        field: target,
        value: percent - ratio,
      });

      // Trigger recursive call to self.
      var self = this;
      this.$data.calc = setTimeout(function(){

        self.minimizeExcessByIncome(endpoint);

      }, .5);

    },



    /**
     * ---------------------------------------------------------------------------- /
     * 
     * Charting Methods
     *
     * These methods are used specifically for handling the charting of the 
     * data tables that are used in the report's projections
     *
     * --------------------------------------------------------------------------- */ 
    

    /**
     * METHOD: chartWealthAccumulation()
     * @return {Object} - data to be used in charting the wealth accumulation chart.
     *
     * This method is used to build the data object that is passed to Chart.js in 
     * order to generate the chart for this section of the report. 
     *
     * The method parses the base wealth table into a format that Chart.js can read,
     * and also includes some basic formatting options for the chart.
     */
    chartWealthAccumulation() {

      var rawData = this.getWealthAccumulationData(),
          chartData = {
            open: {
              label: 'retirementPlanner.savings::slug:savings',
              data: [],
              color: 8,
            },
            lockedin: {
              label: 'retirementPlanner.lockedin::slug:lockedin',
              data: [],
              color: 5,
            },
            rrsp: {
              label: 'retirementPlanner.registered::slug:registered',
              data: [],
              color: 2,
            },
            tfsa: {
              label: 'retirementPlanner.tfsa::slug:tfsa',
              data: [],
              color: 1,
            },            
            labels: [],                                   
          },
          totals = {
            open: false,
            lockedin: false,
            rrsp: false,
            tfsa: false,
            realestate: false,
            other: false,
          };

      for ( var x = 0; x < rawData.length; x++ ) {

        for ( var k in totals ) {

          if ( rawData[x][k] > 0 ) {

            totals[k] = true;
            chartData[k].data[x] = rawData[x][k];

          }

        }

        chartData.labels[x] = rawData[x].age;

      }

      for ( var k in totals ) {

        if ( totals[k] === false ) {

          delete chartData[k];

        }

      }

      return chartData;

    },


    /**
     * METHOD: chartSystematicWithdrawals()
     * @return {Object} - data to be used in charting the wealth de-accumulation chart.
     *
     * This method is used to build the data object that is passed to Chart.js in 
     * order to generate the chart for this section of the report. 
     *
     * The method parses the base wealth table into a format that Chart.js can read,
     * and also includes some basic formatting options for the chart.
     */
    chartSystematicWithdrawals() {

      var rawData = this.getSystematicWidthdrawlsData(),
          chartData = {
            savings: {
              label: 'retirementPlanner.savings::slug:savings',
              data: [],
              color: 8,
            },
            lockedin: {
              label: 'retirementPlanner.lockedin::slug:lockedin',
              data: [],
              color: 5,
            },            
            rrsp: {
              label: 'retirementPlanner.registered::slug:rrsp',
              data: [],
              color: 2,
            },
            tfsa: {
              label: 'retirementPlanner.tfsa::slug:tfsa',
              data: [],
              color: 1,
            },

            labels: [],                                   
          },
          totals = {
            savings: false,
            lockedin: false,
            tfsa: false,
            rrsp: false,
          };

      for ( var x = 0; x < rawData.length; x++ ) {

        for ( var k in totals ) {

          if ( rawData[x][k] > 0 ) {

            totals[k] = true;
            chartData[k].data[x] = rawData[x][k];

          }

        }

        chartData.labels[x] = rawData[x].age;

      }

      for ( var k in totals ) {

        if ( totals[k] === false ) {

          delete chartData[k];

        }

      }

      return chartData;

    },


    /**
     * METHOD: chartPostRetirementCashFlowData()
     * @return {Object} - data to be used in charting the post-retirement cashflows chart.
     *
     * This method is used to build the data object that is passed to Chart.js in 
     * order to generate the chart for this section of the report. 
     *
     * The method parses the base wealth table into a format that Chart.js can read,
     * and also includes some basic formatting options for the chart.
     */
    chartPostRetirementCashFlowData() {

      var rawData = this.getPostRetirementCashFlowData(),
          chartData = {             
            cpp: {
              label: 'retirementPlanner.cpp::slug:cpp',
              data: [],
              color: 3,
            },
            oas: {
              label: 'retirementPlanner.oas::slug:oas',
              data: [],
              color: 0,
            }, 
            pension: {
              label: 'retirementPlanner.pension::slug:pension',
              data: [],
              color: 4,
            },             
            savings: {
              label: 'retirementPlanner.savings::slug:savings',
              data: [],
              color: 8,
            },
            lockedin: {
              label: 'retirementPlanner.lockedin::slug:lockedin',
              data: [],
              color: 5,
            },            
            rrsp: {
              label: 'retirementPlanner.registered::slug:rrsp',
              data: [],
              color: 2,
            },
            tfsa: {
              label: 'retirementPlanner.tfsa::slug:tfsa',
              data: [],
              color: 1,
            },            
            other: {
              label: 'retirementPlanner.other::slug:other',
              data: [],
              color: 2,
            },            
            labels: [],                                   
          },
          totals = {
            savings: false,
            lockedin: false,
            rrsp: false,
            tfsa: false,
            cpp: false,
            oas: false,
            pension: false,
            other: false,
          };

      for ( var x = 0; x < rawData.length; x++ ) {

        for ( var k in totals ) {

          if ( rawData[x][k] > 0 ) {

            totals[k] = true;
            chartData[k].data[x] = rawData[x][k];

          }

        }

        chartData.labels[x] = rawData[x].age;

      }

      for ( var k in totals ) {

        if ( totals[k] === false ) {

          delete chartData[k];

        }

      }

      return chartData;

    },  



    /**
     * ---------------------------------------------------------------------------- /
     * 
     * Deprecated Methods
     *
     * Methods which are no longer used. Commented out, but retained for reference.
     *
     * --------------------------------------------------------------------------- * 

    sumSegments(segments) {

      var total = 0;

      for (var k in segments) {

        if ( segments[k].hasOwnProperty('base') ) {
          total += segments[k].base
        }
        else {
          total += segments[k];
        }

      }

      return total;

    }, 

     * --------------------------------------------------------------------------- */         


  },


  /**
   * Component Data Model
   *
   * This data function establishes the component's data model, by returning an
   * initial object when the component is first rendered. 
   *
   * Various methods will modify the values in the data model, or pull the
   * values in order to control the component's overall interface.
   */
  data: function() {

    return {

      // Determines the chart view for the different reports.
      views: {

        wealthAccumulation:     'table',
        systematicWithdrawals:  'table',
        postRetirementCashFlow: 'table',

      },

      // PARTIALLY DEPRECATED
      // Determines the items to be included in printing.
      print: {

        outlook:                true,
        income:                 true,
        government:             true,
        company:                true,
        regularInvestments:     true,
        lockedinInvestments:    true,
        groupInvestments:       true,
        realEstate:             true,
        wealthAccumulation:     true,
        systematicWithdrawals:  true,
        postRetirementCashFlow: true,

      },      

      // Defines the active blocks
      blocks: {

        outlook:                true,
        income:                 true,
        government:             true,
        company:                true,
        regularInvestments:     true,
        tfsaInvestments:        true,
        lockedinInvestments:    true,
        dpspInvestments:        true,
        groupInvestments:       true,
        realEstate:             true,
        wealthAccumulation:     true,
        systematicWithdrawals:  true,
        postRetirementCashFlow: true,

      },

      // Defines the deacumulations 
      decumulations: {

        lockedin_lockedRrsp:      'lockedin',
        lockedin_employerPension: 'lockedin',

        open_tfsa:                'tfsa',

        registered_rrif:          'rrsp',
        registered_mutualFunds:   'rrsp',
        registered_dpspEsopGrsp:  'lockedin',
        registered_other:         'rrsp',

        open_stocks:              'savings',
        open_mutualFunds:         'savings',
        open_other:               'savings',
        open_savings:             'savings',
        open_chequing:            'savings',

      },

      calc: false, 

      excessThreshold: 0,    

    }

  }

}

</script>

<style lang="scss">

  .report__value {

      color: #06a0df;

      .negative {

        color: #cb2535;

      }

  }

  .retirement__type-heading {

    margin-top: 2.5rem;

  }

  .retirement__total {

    font-size: 1.4em;
    padding: 1rem;

    .report__value {

      font-size: 2em;

      display: block;

    }

  } 

  .retirement__table-wrap {

    overflow-x: auto;

  }

  .retirement__table {

    background: #fff;
    width: 100%;

  }

  .retirement__table-row {

    border-bottom: 1px solid #efefef;

    &:last-child {

      border: 0;

    }

    td {

      &.retirement__table-cell--age {

        color: #06a0df;
        font-size: 1.2rem;
        border-right: 1px solid #efefef;
        padding: .75rem;

      }

    }

    th {

      &.retirement__table-cell {

        padding: .75rem;

      }

    }

  }

  .retirement__table-row--heading {

    background-color: #efefef;

  }

  .retirement__table-cell {

    text-align: left;
    white-space: nowrap;

    &:first-child {

      padding-left: 1rem;

    }

  }

  .retirement__table-detail {

    display: flex;
    border-bottom: 1px solid #efefef;
    padding: .75rem;

    &:last-child {

      border: 0;

    }

  }

  .retirement__table-cell--age {

    width: 3rem;
    vertical-align: top;

  }

  .retirement__table-detail-label {

    font-weight: 700;
    padding-right: .25em;
    width: 5.5rem;

  }

  .retirement__table-detail-value {


  }

  .tabular {

    .retirement__table-cell {

      padding: .75rem;

    }
    
  }

  .section__buttons {

    position: absolute;
    top: 50%;
    right: 1.5rem;
    transform: translate3d(0, -50%, 0);

  }
  .section__button {

    -webkit-appearance: none;
    appearance: none;
    background: none;
    border: 0;
    outline: none;

    padding: 0;
    margin-left: .5rem;

    &.section__button--print {

      &.js-active {

        .icon__svg {
          fill: #06a0df;
          stroke: #06a0df;
        }

      }

    }

    &:hover {

      .icon {

        transform: scale(1);

      }

    }

    &.js-active {

      .icon__svg {

        fill: #000;
        stroke: #000;

      }

    }

    .icon {

      width: 1.1rem;
      height: 1.1rem;
      transform: scale(.85);
      transition: transform .1s ease;

    }

    .icon__svg {

      fill: #999;
      stroke: #999;
      stroke-width: 3;

    }

  }
  

</style>




// WEBPACK FOOTER //
// RetirementPlanner.vue?c28e2dd4